import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useHistory, useLocation, useParams } from "react-router-dom";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import AllInboxOutlinedIcon from "@mui/icons-material/AllInboxOutlined";

import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import NotesIcon from "@mui/icons-material/Notes";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import PercentIcon from "@mui/icons-material/Percent";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SearchIcon from "@mui/icons-material/Search";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import ContactMailOutlinedIcon from "@mui/icons-material/ContactMailOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";

import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";

import { fetchEstimationDetails, updateEstimation, updateEstimationInHandsDate } from "../../services/estimation.js";
import { getTerms } from "../.../../../services/terms";
import { getTaxes } from "../.../../../services/tax";
import ButtonLoader from "../ButtonLoader/index.js";
import CustomerContactDialog from "../CustomerContactDialog";
import {
  fetchCustomerTermChangeRequests,
  createCustomerContact,
  getCustomerDetails,
} from "../../services/customer/index.js";
import toast from "react-hot-toast";
import { validateForm } from "./index.controller.js";
import LoadingComponent from "../ComponentLoader";
import { fetchSuppliersList } from "../.../../../services/suppliers/index.js";
import { fetchAddressList } from "../.../../../services/address";
import { fetchInvoicesListByOrder } from "../.../../../services/invoices";
import AddCustomerAddressModal from "../Estimation/AddressManager";
import { AuthContext } from "../../helpers/AuthContext";
import { normalizeInvoiceRoles } from '../../utils/invoicePermissions';
import { getManualLateSalesOrderStatuses } from '../../utils/manualSalesOrderStatusPolicy';
import { getAllClientAccounts } from "../../services/shipping";
import ThirdPartyShippingAccount from "./ThirdPartyShippingAccount";
import TermsChangeRequestDialog from "./TermsChangeRequestDialog";
import {
  SAMPLE_ORDER_UPFRONT_DISABLED_HELPER,
  canEditSampleOrderStatus,
  getSampleOrderDialogContent,
  getSampleOrderStageHelperText,
} from "./sampleOrder";
import {
  findPendingTermsRequest,
  getPendingTermsRequestLabel,
  resolveAttachedCustomerId,
  resolveAttachedCustomerName,
  shouldShowTermsRequestButton,
} from "./termsChangeRequestHelpers";
import {
  getSalesOrderEditStatusMessage,
  normalizeSalesOrderEditStatus,
} from "./salesOrderEditStatus";

dayjs.extend(utc);

const currencyOptions = [
  { id: "USD", name: "USD", country: "us" },
  { id: "EUR", name: "EUR", country: "eu" },
  { id: "GBP", name: "GBP", country: "gb" },
  { id: "CAD", name: "CAD", country: "ca" },
  { id: "INR", name: "INR", country: "in" },
];





const FLAG_BASE = 'https://flagcdn.com';

const inferFlagCode = (opt = {}) => {
  const explicit = (opt.flag || opt.country || '').toString().trim().toLowerCase();
  if (explicit) return explicit;
  switch ((opt.id || '').toUpperCase()) {
    case 'USD': return 'us';
    case 'EUR': return 'eu';
    case 'GBP': return 'gb';
    case 'CAD': return 'ca';
    case 'INR': return 'in';
    case 'AUD': return 'au';
    case 'NZD': return 'nz';
    case 'JPY': return 'jp';
    case 'CNY': return 'cn';
    default:    return '';
  }
};

const FlagIcon = ({ code, size = 20, alt = '' }) => {
  if (!code) return null;
  return (
    <img
      src={`${FLAG_BASE}/w${size}/${code}.png`}
      alt={alt}
      width={size}
      height={Math.round(size * 0.75)}
      style={{ display: 'inline-block', borderRadius: 2 }}
      loading="lazy"
      onError={(e) => { e.currentTarget.style.display = 'none'; }}
    />
  );
};


const ymdFromAnything = (v) => {
  if (!v) return null;
  const s = String(v).trim();
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : null;
};

const coerceDayjs = (v) => {
  if (!v) return null;
  if (dayjs.isDayjs(v)) return v.isValid() ? v : null;
  if (v instanceof Date) {
    const d = dayjs(v);
    return d.isValid() ? d : null;
  }
  const ymd = ymdFromAnything(v);
  if (ymd) {
    const d = dayjs(ymd);
    return d.isValid() ? d : null;
  }
  const d = dayjs(String(v));
  return d.isValid() ? d : null;
};


const hasUsableId = (value) =>
  value !== null &&
  value !== undefined &&
  String(value).trim() !== "" &&
  !["undefined", "null"].includes(String(value).trim().toLowerCase());

const getCustomerAddressId = (a = {}) =>
  a.id ?? a.addressId ?? a.customerAddressId ?? a.AddressId ?? null;

const getCustomerContactId = (c = {}) =>
  c.id ?? c.clientContactId ?? c.contactId ?? c.uuid ?? c.ClientContactId ?? null;

const toCustomerAddressOptionId = (id) => (hasUsableId(id) ? `cust-${id}` : "");
const toCustomerContactOptionId = (id) => (hasUsableId(id) ? `cus-${id}` : "");

const FieldSavingSpinner = ({ active, inline = false }) =>
  active ? (
    <CircularProgress
      size={16}
      thickness={5}
      sx={inline
        ? {
            flex: "0 0 auto",
            pointerEvents: "none",
          }
        : {
            position: "absolute",
            top: 10,
            right: 10,
            zIndex: 3,
            pointerEvents: "none",
          }}
    />
  ) : null;


function parseLocalDate(isoString) {
  if (!isoString) return dayjs();
  const [y, m, d] = isoString.split("T")[0].split("-").map(Number);
  return dayjs(new Date(y, m - 1, d));
}

const isTruthyFlag = (v) =>
  v === true || v === "true" || v === 1 || v === "1";

const normalizeRequireUpfront = (v) => {
  if (
    v == null ||
    v === "not_required" ||
    v === false ||
    v === "false" ||
    v === 0 ||
    v === "0"
  )
    return null;
  return isTruthyFlag(v) ? true : null;
};

const normalizeBool = (v, fallback = true) => {
  if (v == null) return fallback;
  return isTruthyFlag(v);
};

const normalizePaymentStatusLabel = (status) => {
  const s = String(status || "").trim().toLowerCase();
  if (!s) return "";
  if (["paid", "completed", "succeeded", "success"].includes(s)) return "Paid";
  if (
    ["open", "pending", "required", "requires_payment", "payment_required", "unpaid"].includes(
      s
    )
  )
    return "Open";
  if (["not_required", "n/a", "na"].includes(s)) return "Not Required";
  if (["failed", "canceled", "cancelled", "void"].includes(s)) return "Failed";
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const getCustomerContactDisplayName = (c = {}) =>
  c?.name ||
  [c?.firstName, c?.lastName].filter(Boolean).join(' ') ||
  [c?.first_name, c?.last_name].filter(Boolean).join(' ') ||
  c?.email ||
  c?.Email ||
  'Contact';

const resolveSavedCustomerContactId = (savedId, customerContacts = [], savedDisplay = '') => {
  if (!savedId && !savedDisplay) return '';

  const raw = String(savedId || '').trim();

  if (raw && customerContacts.some((c) => String(c.id) === raw)) {
    return raw;
  }

  const display = String(savedDisplay || '').trim().toLowerCase();
  if (display) {
    const byNameOrEmail = customerContacts.find((c) =>
      String(c?.name || '').trim().toLowerCase() === display ||
      String(c?.email || '').trim().toLowerCase() === display
    );
    if (byNameOrEmail?.id) return String(byNameOrEmail.id);
  }

  const legacyMatch = raw.match(/^cus-(\d+)$/);
  if (legacyMatch) {
    const idx = Number(legacyMatch[1]);
    const legacyOption = customerContacts[idx];
    if (legacyOption?.id) return String(legacyOption.id);
  }

  return '';
};

const buildCustomerContactOption = (contact = {}) => {
  const contactId = getCustomerContactId(contact);
  const optionId = toCustomerContactOptionId(contactId);

  if (!optionId) return null;

  return {
    id: optionId,
    name: getCustomerContactDisplayName(contact),
    email: contact.email || contact.Email || "",
    phone: contact.phone || contact.Phone || "",
    type: "client",
    source: "customer",
    raw: contact,
  };
};

const mergeOptionsById = (options = []) => {
  const next = new Map();
  options.forEach((option) => {
    if (!option?.id) return;
    next.set(String(option.id), option);
  });
  return Array.from(next.values());
};

const resolveSavedCustomerAddressId = (savedId, customerAddresses = []) => {
  if (!savedId) return '';

  const raw = String(savedId).trim();

  // Already correct/current.
  if (customerAddresses.some((a) => String(a.id) === raw)) {
    return raw;
  }

  // Legacy cust-index support only.
  const legacyMatch = raw.match(/^cust-(\d+)$/);
  if (legacyMatch) {
    const idx = Number(legacyMatch[1]);
    const legacyOption = customerAddresses[idx];
    if (legacyOption?.id) return String(legacyOption.id);
  }

  return '';
};


const normalizeSoWorkflowStageFromStatus = (status) => {
  const raw = String(status || '').trim();
  if (raw === '1_sent') return 'Estimate Sent';
  if (raw === '2_sent') return 'Agreement Sent';
  if (raw === '3') return 'Client Approved';
  if (raw === '4') return 'Client Change Requested';
  if (raw === '5') return 'Closed';
  if (raw === '6') return 'In Production';
  if (raw === '7') return 'Shipped';
  if (raw === '8') return 'Ready to be Invoiced';
  if (raw === '9') return 'Invoiced';
  return '';
};

const normalizeSoWorkflowStage = (raw) => {
  const value = String(raw || "").trim();
  const key = value.toLowerCase();
  const aliasMap = {
    estimate: "Estimate Draft",
    "estimate draft": "Estimate Draft",
    "estimate sent": "Estimate Sent",
    "sales order": "Agreement Pending",
    "sales order draft": "Agreement Pending",
    "sales order sent": "Agreement Sent",
    "sent for agreement": "Agreement Pending",
    "agreement pending": "Agreement Pending",
    "agreement sent": "Agreement Sent",
    "sales order - agreement": "Agreement Pending",
    "sales order - customer signed": "Client Approved",
    "customer signed": "Client Approved",
    completed: "Client Approved",
    "client approved": "Client Approved",
    "sales order ready": "Client Approved",
    rejected: "Client Change Requested",
    "changes requested": "Client Change Requested",
    "client change requested": "Client Change Requested",
  };
  return aliasMap[key] || value || "Unknown";
};

const buildDisplay = (a = {}) => {
  const line1 = a.street || a.streetAddress || "";
  const suiteLine = a.suiteApt || "";
  const line2 = [a.city, a.state || a.stateProvince].filter(Boolean).join(", ");
  const parts = [
    a.addressName,
    line1,
    suiteLine,
    line2,
    a.postal || a.postalZipCode,
    a.country,
  ].filter(Boolean);
  return parts.join(" · ");
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    height: 40,
    borderRadius: "8px",
    backgroundColor: "background.paper",
    fontSize: "0.875rem",
    "& fieldset": { borderColor: "divider" },
    "&:hover fieldset": { borderColor: "text.disabled" },
    "&.Mui-focused fieldset": {
      borderColor: "primary.main",
      borderWidth: "1.5px",
    },
    "&.Mui-disabled": {
      backgroundColor: "action.hover",
    },
  },
  "& .MuiInputBase-input": {
    padding: "0 12px",
    height: 40,
    boxSizing: "border-box",
  },
};

const selectSx = {
  height: 40,
  borderRadius: "8px",
  fontSize: "0.875rem",
  backgroundColor: "background.paper",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "divider",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "text.disabled",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "primary.main",
    borderWidth: "1.5px",
  },
  "&.Mui-disabled": {
    backgroundColor: "action.hover",
  },
  "& .MuiSelect-icon": {
    fontSize: 18,
    color: "text.secondary",
  },
};

const datePickerSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    height: 40,
    borderRadius: "8px",
    backgroundColor: "background.paper",
    fontSize: "0.875rem",
    "& fieldset": { borderColor: "divider" },
    "&:hover fieldset": { borderColor: "text.disabled" },
    "&.Mui-focused fieldset": {
      borderColor: "primary.main",
      borderWidth: "1.5px",
    },
  },
  "& .MuiInputBase-input": {
    padding: "0 12px",
    height: 40,
    boxSizing: "border-box",
    fontSize: "0.875rem",
  },
};

const SectionCard = ({
  icon: Icon,
  title,
  subtitle,
  action,
  children,
  accent = false,
}) => (
  <Paper
    elevation={0}
    sx={{
      border: "1px solid",
      borderColor: "divider",
      borderRadius: "10px",
      overflow: "hidden",
      boxShadow:
        "0 1px 3px 0 rgba(0,0,0,0.05), 0 1px 2px -1px rgba(0,0,0,0.05)",
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 2,
        py: 1.4,
        borderBottom: "1px solid",
        borderColor: "divider",
        bgcolor: accent ? "rgba(199,9,9,0.02)" : "background.paper",
        gap: 1,
        flexWrap: "wrap",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: "7px",
            bgcolor: accent ? "rgba(199,9,9,0.08)" : "action.hover",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon
            sx={{
              fontSize: 15,
              color: accent ? "primary.main" : "text.secondary",
            }}
          />
        </Box>

        <Box>
          <Typography
            variant="body2"
            fontWeight={700}
            sx={{ lineHeight: 1.2, color: "text.primary", fontSize: "0.875rem" }}
          >
            {title}
          </Typography>
          {subtitle ? (
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", fontSize: "0.72rem", lineHeight: 1.3 }}
            >
              {subtitle}
            </Typography>
          ) : null}
        </Box>
      </Stack>

      {action ? <Box>{action}</Box> : null}
    </Box>

    <Box sx={{ p: 2.25 }}>{children}</Box>
  </Paper>
);

const FieldLabel = ({ children, required, hint, action }) => (
  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.65 }}>
    <Typography
      variant="caption"
      sx={{
        color: "text.secondary",
        fontWeight: 600,
        fontSize: "0.74rem",
        lineHeight: 1,
      }}
    >
      {children}
    </Typography>
    {required ? (
      <Chip
        label="Required"
        size="small"
        sx={{
          height: 16,
          fontSize: "0.58rem",
          fontWeight: 700,
          bgcolor: "rgba(199,9,9,0.07)",
          color: "primary.main",
          border: "1px solid rgba(199,9,9,0.14)",
          "& .MuiChip-label": { px: "5px" },
        }}
      />
    ) : null}
    {hint ? (
      <Tooltip title={hint} arrow placement="top">
        <InfoOutlinedIcon
          sx={{ fontSize: 12, color: "text.disabled", cursor: "help" }}
        />
      </Tooltip>
    ) : null}
    {action ? (
      <Box sx={{ ml: "auto !important", display: "flex", alignItems: "center", gap: 0.75 }}>
        {action}
      </Box>
    ) : null}
  </Stack>
);


const AddressField = ({
  label,
  value,
  onChange,
  options = [],
  onAddNew,
  disabled,
  emptyText = "Select address",
  helperText,
  saving = false,
}) => {
  const selected = options.find((o) => String(o.id) === String(value));

  return (
    <Box sx={{ position: "relative" }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 0.65 }}
      >
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontWeight: 600,
            fontSize: "0.74rem",
          }}
        >
          {label}
        </Typography>

        <Stack direction="row" alignItems="center" spacing={0.75}>
          <FieldSavingSpinner active={saving} inline />
          {onAddNew ? (
          <Tooltip title={`Add new ${label.toLowerCase()}`} arrow>
            <IconButton
              size="small"
              onClick={onAddNew}
              disabled={disabled}
              sx={{
                p: 0.35,
                color: "text.disabled",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "7px",
                "&:hover": {
                  color: "primary.main",
                  borderColor: "primary.light",
                  bgcolor: "action.hover",
                },
              }}
            >
              <AddCircleOutlineIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          ) : null}
        </Stack>
      </Stack>

      <Select
        fullWidth
        size="small"
        value={value ?? ""}
        onChange={(e) => onChange?.(e.target.value)}
        displayEmpty
        disabled={disabled || saving}
        IconComponent={KeyboardArrowDownIcon}
        renderValue={(val) => {
          const found = options.find((o) => String(o.id) === String(val));
          return found ? (
            <Typography sx={{ fontSize: "0.875rem", color: "text.primary" }}>
              {found.name || found.display}
            </Typography>
          ) : (
            <Typography sx={{ fontSize: "0.875rem", color: "text.disabled" }}>
              {emptyText}
            </Typography>
          );
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              borderRadius: "8px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
              mt: 0.5,
              maxHeight: 280,
            },
          },
        }}
        sx={selectSx}
      >
        <MenuItem value="">
          <Typography sx={{ fontSize: "0.84rem", color: "text.disabled" }}>
            {emptyText}
          </Typography>
        </MenuItem>

        {options.map((opt) => (
          <MenuItem key={opt.id} value={String(opt.id)}>
            <Box>
              <Typography
                sx={{
                  fontSize: "0.84rem",
                  fontWeight: 600,
                  lineHeight: 1.2,
                }}
              >
                {opt.name}
              </Typography>
              {opt.display && opt.display !== opt.name ? (
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    color: "text.secondary",
                    lineHeight: 1.3,
                    mt: 0.2,
                    whiteSpace: "normal",
                  }}
                >
                  {opt.display}
                </Typography>
              ) : null}
            </Box>
          </MenuItem>
        ))}
      </Select>

      {/* {(selected?.display || helperText) && (
        <Box
          sx={{
            mt: 0.75,
            px: 1.25,
            py: 0.9,
            borderRadius: "8px",
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "action.hover",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              display: "block",
              color: "text.secondary",
              fontSize: "0.68rem",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: 0.35,
              mb: 0.35,
            }}
          >
            Selected
          </Typography>

          <Typography
            variant="body2"
            sx={{
              fontSize: "0.78rem",
              color: "text.primary",
              lineHeight: 1.45,
              wordBreak: "break-word",
            }}
          >
            {selected?.display || helperText}
          </Typography>
        </Box>
      )} */}

        {selected?.display || helperText ? (
        <Box
          sx={{
            mt: 0.75,
            px: 1.25,
            py: 0.9,
            borderRadius: "8px",
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "action.hover",
            minHeight: 72,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              display: "block",
              color: "text.secondary",
              fontSize: "0.68rem",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: 0.35,
              mb: 0.35,
            }}
          >
            Selected
          </Typography>

          <Typography
            variant="body2"
            sx={{
              fontSize: "0.78rem",
              color: "text.primary",
              lineHeight: 1.45,
              wordBreak: "break-word",
            }}
          >
            {selected?.display || helperText}
          </Typography>
        </Box>
      ) : (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 0.85,
            px: 0.25,
            fontSize: "0.72rem",
            color: "text.disabled",
            lineHeight: 1.4,
          }}
        >
          No address selected yet.
        </Typography>
      )}





    </Box>
  );
};

const DepositBadge = ({ status }) => {
  const norm = String(status || "").toLowerCase();
  const isPaid = ["paid", "completed", "succeeded", "success"].includes(norm);
  const isOpen = ["open", "pending", "required", "unpaid"].includes(norm);
  if (!norm || norm === "notrequired" || norm === "not required") return null;

  return (
    <Chip
      size="small"
      label={isPaid ? "Paid" : isOpen ? "Open" : normalizePaymentStatusLabel(status)}
      icon={
        isPaid ? (
          <CheckCircleOutlineIcon sx={{ fontSize: "13px !important" }} />
        ) : (
          <PaidOutlinedIcon sx={{ fontSize: "13px !important" }} />
        )
      }
      sx={{
        height: 22,
        fontSize: "0.72rem",
        fontWeight: 700,
        bgcolor: isPaid ? "rgba(34,197,94,0.08)" : "rgba(234,179,8,0.10)",
        color: isPaid ? "#15803d" : "#92400e",
        border: `1px solid ${
          isPaid ? "rgba(34,197,94,0.25)" : "rgba(234,179,8,0.3)"
        }`,
        "& .MuiChip-label": { px: "7px" },
        "& .MuiChip-icon": { ml: "6px" },
      }}
    />
  );
};

const statusMeta = {
  "Estimate Draft": {
    color: "#64748b",
    bg: "rgba(100,116,139,0.07)",
    border: "rgba(100,116,139,0.2)",
    label: "Estimate Draft",
    desc: "Internal draft — review details and send when ready.",
  },
  "Estimate Sent": {
    color: "#2563eb",
    bg: "rgba(37,99,235,0.07)",
    border: "rgba(37,99,235,0.2)",
    label: "Estimate Sent",
    desc: "The estimate has been shared with the client.",
  },
  "Agreement Pending": {
    color: "#d97706",
    bg: "rgba(217,119,6,0.07)",
    border: "rgba(217,119,6,0.2)",
    label: "Agreement Pending",
    desc: "Sales order draft — not yet sent to the client.",
  },
  "Agreement Sent": {
    color: "#7c3aed",
    bg: "rgba(124,58,237,0.07)",
    border: "rgba(124,58,237,0.2)",
    label: "Agreement Sent",
    desc: "Awaiting client approval — ask them to click Agree.",
  },
  "Client Approved": {
    color: "#16a34a",
    bg: "rgba(22,163,74,0.07)",
    border: "rgba(22,163,74,0.2)",
    label: "Client Approved",
    desc: "Completed — ready to generate purchase orders.",
  },
  "Client Change Requested": {
    color: "#dc2626",
    bg: "rgba(220,38,38,0.07)",
    border: "rgba(220,38,38,0.2)",
    label: "Change Requested",
    desc: "Client requested revisions. Update and resend when ready.",
  },
  Closed: {
    color: "#64748b",
    bg: "rgba(100,116,139,0.07)",
    border: "rgba(100,116,139,0.2)",
    label: "Closed",
    desc: "This order has been closed and is no longer active.",
  },
  "In Production": {
    color: "#0891b2",
    bg: "rgba(8,145,178,0.07)",
    border: "rgba(8,145,178,0.2)",
    label: "In Production",
    desc: "The order is currently in production.",
  },
  Shipped: {
    color: "#16a34a",
    bg: "rgba(22,163,74,0.07)",
    border: "rgba(22,163,74,0.2)",
    label: "Shipped",
    desc: "The order has been shipped to the client.",
  },
  "Ready to be Invoiced": {
    color: "#d97706",
    bg: "rgba(217,119,6,0.07)",
    border: "rgba(217,119,6,0.2)",
    label: "Ready to Invoice",
    desc: "Order is complete and ready for invoice generation.",
  },
  Invoiced: {
    color: "#7c3aed",
    bg: "rgba(124,58,237,0.07)",
    border: "rgba(124,58,237,0.2)",
    label: "Invoiced",
    desc: "Invoice has been issued for this order.",
  },
  Paid: {
    color: "#16a34a",
    bg: "rgba(22,163,74,0.07)",
    border: "rgba(22,163,74,0.2)",
    label: "Paid",
    desc: "Payment received. Order complete.",
  },
};

const WorkflowBanner = ({ stage, approvedMeta }) => {
  const meta = statusMeta[stage] || statusMeta["Estimate Draft"];

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2,
        py: 1.25,
        borderRadius: "10px",
        border: "1px solid",
        borderColor: meta.border,
        bgcolor: meta.bg,
      }}
    >
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          bgcolor: meta.color,
          flexShrink: 0,
          boxShadow: `0 0 0 3px ${meta.bg}`,
        }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          fontWeight={700}
          sx={{ color: meta.color, fontSize: "0.82rem", lineHeight: 1.2 }}
        >
          {meta.label}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", fontSize: "0.72rem" }}
        >
          {meta.desc}
        </Typography>
      </Box>

      {stage === "Client Approved" && approvedMeta?.name ? (
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
          <Avatar
            sx={{
              width: 22,
              height: 22,
              fontSize: "0.6rem",
              bgcolor: "rgba(22,163,74,0.15)",
              color: "#16a34a",
            }}
          >
            {approvedMeta.name?.[0]?.toUpperCase?.() || "A"}
          </Avatar>
          <Box>
            <Typography
              variant="caption"
              sx={{ fontSize: "0.7rem", fontWeight: 600, display: "block", lineHeight: 1 }}
            >
              {approvedMeta.name}
            </Typography>
            {approvedMeta.at ? (
              <Typography
                variant="caption"
                sx={{ fontSize: "0.65rem", color: "text.secondary", lineHeight: 1 }}
              >
                {dayjs(approvedMeta.at).format("MMM D, YYYY")}
              </Typography>
            ) : null}
          </Box>
        </Stack>
      ) : null}
    </Box>
  );
};

const resolveDraftStatusFromConversionStatus = (conversionStatus) =>
  String(conversionStatus) === "2"
    ? "2"
    : "1";

const Estimation = ({
  clientContacts,
  orderId,
  order,
  fetchOrder,
  conversionStatus = 1,
  paidDepositApprovalAction = null,
}) => {
  const { tabId, instanceUniqueId: routeInstanceUniqueId, id: legacyInstanceUniqueId } = useParams();
  const location = useLocation();
  const history = useHistory();
  // Instance routes use :instanceUniqueId; older nested routes can expose :id.
  // Preserve the actual URL slug when promoting an estimate to a sales order.
  const instanceUniqueId = routeInstanceUniqueId || legacyInstanceUniqueId ||
    location.pathname.match(/^\/instance\/([^/]+)\//)?.[1] || null;
  const { authState } = useContext(AuthContext);

  const roleSet = normalizeInvoiceRoles(authState?.user || authState || {});
  const isAdmin = roleSet.has('administrator');
  const canManageLateSalesOrderStatus = getManualLateSalesOrderStatuses(roleSet).length > 0;
  const isSalesOrderUrl = String(location?.pathname || "").includes("/sales-order/");
  const customerId = resolveAttachedCustomerId(order);
  const routeCreatedDocument = location?.state?.createdDocument || null;
  const optimisticCreatedDocumentId = String(
    routeCreatedDocument?.estimationId ??
      routeCreatedDocument?.id ??
      ""
  ).trim();
  const optimisticCreatedStatus = String(routeCreatedDocument?.status ?? "").trim();
  const createModeStatus = resolveDraftStatusFromConversionStatus(conversionStatus);
  const isOptimisticCreatedDocument =
    !!String(tabId || "").trim() &&
    !!optimisticCreatedDocumentId &&
    optimisticCreatedDocumentId === String(tabId || "").trim() &&
    !!optimisticCreatedStatus;
  const initialStatus = isOptimisticCreatedDocument
    ? optimisticCreatedStatus
    : createModeStatus;

  const [termsOptions, setTermsOptions] = useState([]);
  const [taxOptions, setTaxOptions] = useState([]);
  const [customerDisplayName, setCustomerDisplayName] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingComponent, setComponentLoading] = useState(true);
  const [contactsOptions, setContactsOptions] = useState([]);
  const [customerContactsOptions, setCustomerContactsOptions] = useState([]);
  const [clientModalOpen, setClientModelOpen] = useState(false);
  const [approvedMeta, setApprovedMeta] = useState({ name: "", email: "", at: null });
  const [depositInfo, setDepositInfo] = useState(null);
  const [removingUpfrontPayment, setRemovingUpfrontPayment] = useState(false);
  const [addAddrOpen, setAddAddrOpen] = useState(false);
  const [addrTarget, setAddrTarget] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [allCustomerAddresses, setAllCustomerAddresses] = useState([]);
  const [allGlobalAddresses, setAllGlobalAddresses] = useState([]);
  const [addressScope, setAddressScope] = useState("customer");
  const [addressQuery, setAddressQuery] = useState("");
  const [hydrated, setHydrated] = useState(false);
  const [statusStageOverride, setStatusStageOverride] = useState(null);
  const [termsRequestOpen, setTermsRequestOpen] = useState(false);
  const [pendingTermsRequest, setPendingTermsRequest] = useState(null);
  const [contactDialog, setContactDialog] = useState({
    open: false,
    target: null,
    loading: false,
  });

  const originalStatusRef = useRef(null);

  const statusOptions = [
    { id: "1", name: "Estimate (Estimate Draft)" },
    { id: "1_sent", name: "Estimate (Estimate Sent)", disabled: true },
    { id: "2", name: "Sales Order (Agreement Pending)" },
    { id: "2_sent", name: "Sales Order (Agreement Sent)", disabled: true },
    { id: "3", name: "Client Approved", disabled: true },
    { id: "4", name: "Client Change Requested", disabled: true },
    { id: "5", name: "Closed", disabled: true },
  ];

  const salesOrderLifecycleOptions = [
    { id: "6", name: "In Production", disabled: true },
    { id: "7", name: "Shipped", disabled: true },
    { id: "8", name: "Ready to be Invoiced", disabled: true },
    { id: "9", name: "Invoiced", disabled: true },
  ];


  const addressScopeLabels = {
  customer: "Customer",
  global: "Global",
  all: "All",
};

  const [formData, setFormData] = useState({
    status: initialStatus,
    commentsEnabled: true,
    orderDate: parseLocalDate(),
    introduction: "",
    billingAddress: "",
    billingAddressDisplay: "",
    billingClientName: "",
    billingClientNameDisplay: "",
    shippingAddress: "",
    shippingAddressDisplay: "",
    shippingClientName: "",
    shippingClientNameDisplay: "",
    selectedBillingAddress: null,
    selectedMainShippingAddress: null,
    terms: null,
    currency: "CAD",
    inHandsDate: parseLocalDate(),
    defaultMargin: "",
    defaultTax: null,
    customerPO: "",
    shippingTo: "client",
    requireUpfrontPayment: null,
    upfrontMode: null,
    depositType: null,
    depositValue: "",
    paymentDueOnSign: true,
    depositInvoiceId: null,
    depositAmountSnap: null,
    depositCurrencySnap: null,
    paymentStatusSnap: null,
    isSampleOrder: false,
  });
  const [sampleOrderSaving, setSampleOrderSaving] = useState(false);
  const [sampleOrderDialog, setSampleOrderDialog] = useState({
    open: false,
    nextValue: false,
  });

  const [fieldSaving, setFieldSaving] = useState({
    billingAddress: false,
    billingClientName: false,
    shippingAddress: false,
    shippingClientName: false,
    inHandsDate: false,
  });

  const fieldSaveSeqRef = useRef({});
  const fieldSaveTimerRef = useRef({});
  const inHandsDateSaveInFlightRef = useRef(false);

  const openAddAddress = (target) => {
    setAddrTarget(target);
    setAddAddrOpen(true);
  };

  const openAddContactDialog = (target) => {
    if (!customerId) {
      toast.error("Attach a customer to this order before creating a contact.");
      return;
    }
    setContactDialog({ open: true, target, loading: false });
  };

  const closeAddContactDialog = () => {
    if (contactDialog.loading) return;
    setContactDialog({ open: false, target: null, loading: false });
  };

  const openThirdPartyModal = useCallback(() => setClientModelOpen(true), []);
  const closeThirdPartyModal = useCallback(() => setClientModelOpen(false), []);

  const allAddresses = useMemo(() => {
    const m = new Map();
    [...allCustomerAddresses, ...allGlobalAddresses].forEach((o) =>
      m.set(String(o.id), o)
    );
    return Array.from(m.values());
  }, [allCustomerAddresses, allGlobalAddresses]);

  const contactDialogTargetLabel =
    contactDialog.target === "shipping" ? "Main Shipping Contact" : "Billing Contact";

  const latestApprove = useMemo(() => {
    const arr = Array.isArray(auditLog) ? auditLog : [];
    const agrees = arr.filter((e) => e?.type === "agree");
    if (!agrees.length) return null;
    agrees.sort((a, b) => new Date(a?.at || 0) - new Date(b?.at || 0));
    const last = agrees[agrees.length - 1];

    const email = last?.agreedBy?.email || last?.actor?.email || "";
    const name =
      last?.agreedBy?.name ||
      last?.actor?.name ||
      (email ? email.split("@")[0] : "");

    return { name, email, at: last?.at || null };
  }, [auditLog]);

  const toBool = (v, fallback = true) => {
    if (v === true || v === "true" || v === 1 || v === "1") return true;
    if (v === false || v === "false" || v === 0 || v === "0") return false;
    return fallback;
  };

  const handleToggle = (name) => (e) => {
    if (typeof isFieldEditable === "function" && !isFieldEditable(name)) return;
    const checked = !!e.target.checked;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const isAutoSaveField = (name) =>
    ["billingAddress", "billingClientName", "shippingAddress", "shippingClientName"].includes(name);

  const buildSingleFieldPatch = ({ fieldName, nextValue, nextDisplay }) => {
    const patch = {
      orderId,
      status: formData.status,
    };

    if (fieldName === "billingAddress") {
      patch.billingAddressId = nextValue || null;
      patch.billingAddress = nextDisplay || "";
    }
    if (fieldName === "shippingAddress") {
      patch.shippingAddressId = nextValue || null;
      patch.shippingAddress = nextDisplay || "";
    }
    if (fieldName === "billingClientName") {
      patch.billingContactId = nextValue || null;
      patch.billingName = nextDisplay || "";
    }
    if (fieldName === "shippingClientName") {
      patch.shippingContactId = nextValue || null;
      patch.shippingName = nextDisplay || "";
    }

    return patch;
  };

  const saveSingleField = ({ fieldName, nextValue, nextDisplay, rollback }) => {
    if (!tabId || !isAutoSaveField(fieldName)) return;

    if (
      (fieldName === "billingClientName" || fieldName === "shippingClientName") &&
      /undefined|null/i.test(String(nextValue || ""))
    ) {
      toast.error("Unable to save contact because the selected contact is missing an id.");
      return;
    }

    const key = fieldName;
    const seq = (fieldSaveSeqRef.current[key] || 0) + 1;
    fieldSaveSeqRef.current[key] = seq;

    if (fieldSaveTimerRef.current[key]) {
      clearTimeout(fieldSaveTimerRef.current[key]);
    }

    fieldSaveTimerRef.current[key] = setTimeout(async () => {
      try {
        setFieldSaving((prev) => ({ ...prev, [key]: true }));
        const payload = buildSingleFieldPatch({ fieldName, nextValue, nextDisplay });
        await updateEstimation(tabId, payload);

        if (fieldSaveSeqRef.current[key] !== seq) return;

        // Keep the parent Sales Order record in sync with inline billing/shipping
        // autosaves so Actions -> Send Email validates against the latest values
        // without requiring a hard refresh.
        if (typeof fetchOrder === "function") {
          await fetchOrder({ suppressLoader: true });
        }

        if (fieldSaveSeqRef.current[key] !== seq) return;
        toast.success("Saved");
      } catch (error) {
        if (fieldSaveSeqRef.current[key] !== seq) return;
        setFormData((prev) => ({ ...prev, ...rollback }));
        toast.error(`Couldn't save ${key}. Reverted.`);
      } finally {
        if (fieldSaveSeqRef.current[key] === seq) {
          setFieldSaving((prev) => ({ ...prev, [key]: false }));
        }
      }
    }, 200);
  };

  const handleInputChange = (event, name) => {
    if (typeof isFieldEditable === "function" && !isFieldEditable(name)) return;
    const value = event?.target?.value;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (date, name) => {
    if (typeof isFieldEditable === "function" && !isFieldEditable(name)) return;
    setFormData((prev) => ({ ...prev, [name]: date }));
  };

  const handleInHandsDateChange = async (nextDate) => {
    if (typeof isFieldEditable === "function" && !isFieldEditable("inHandsDate")) return;
    if (!tabId || inHandsDateSaveInFlightRef.current) return;

    const normalizedDate = coerceDayjs(nextDate);
    const previousDate = formData.inHandsDate;
    const dateOnly = normalizedDate?.isValid()
      ? normalizedDate.format("YYYY-MM-DD")
      : null;

    setFormData((prev) => ({ ...prev, inHandsDate: normalizedDate }));
    inHandsDateSaveInFlightRef.current = true;
    setFieldSaving((prev) => ({ ...prev, inHandsDate: true }));

    try {
      const response = await updateEstimationInHandsDate(tabId, dateOnly);
      const persistedDate = response?.data?.estimation?.inhandsDate;
      setFormData((prev) => ({
        ...prev,
        inHandsDate: persistedDate == null ? null : parseLocalDate(persistedDate),
      }));
      toast.success("In Hands Date saved.");
    } catch (error) {
      setFormData((prev) => ({ ...prev, inHandsDate: previousDate }));
      toast.error(error?.response?.data?.message || "Couldn't save In Hands Date. Reverted.");
    } finally {
      inHandsDateSaveInFlightRef.current = false;
      setFieldSaving((prev) => ({ ...prev, inHandsDate: false }));
    }
  };

  const handleSelect = (event, name) => {
    const value = event.target.value;

    if (typeof isFieldEditable === "function" && !isFieldEditable(name)) return;

    if (name === "status" && (value === "1_sent" || value === "2_sent")) return;

    if (name === "status") {
      const loadedStatus = String(originalStatusRef.current ?? "");
      const wasSalesOrderFlow = ["2", "3", "4", "5", "6", "7", "8", "9"].includes(
        loadedStatus
      );

      if (wasSalesOrderFlow && String(value) === "1") {
        if (loadedStatus !== "2") return;

        const confirmed = window.confirm(
          "Return this Sales Order to an Estimate? The document will move back to Estimate Draft. Any unpaid active deposit invoice will be voided; paid or partially paid deposits must be resolved before this change."
        );
        if (!confirmed) return;
      }

      const stageByStatus = {
        "1": "Estimate Draft",
        "2": "Agreement Pending",
        "3": "Client Approved",
        "4": "Client Change Requested",
        "5": "Closed",
        "6": "In Production",
        "7": "Shipped",
        "8": "Ready to be Invoiced",
        "9": "Invoiced",
      };
      setStatusStageOverride(stageByStatus[String(value)] || null);
    }

    if (name === "commentsEnabled") {
      const boolVal = value === true || String(value) === "true";
      setFormData((prev) => ({ ...prev, commentsEnabled: boolVal }));
      return;
    }

    if (name === "billingClientName" || name === "shippingClientName") {
      if (/undefined|null/i.test(String(value || ""))) {
        toast.error("Unable to save contact because the selected contact is missing an id.");
        return;
      }

      const selected = contactsOptions.find((c) => String(c.id) === String(value));
      const display = selected?.name || "";
      const rollback = {
        [name]: formData[name],
        [`${name}Display`]: formData?.[`${name}Display`] || "",
        ...(name === "shippingClientName" ? { shippingTo: formData.shippingTo } : {}),
      };

      setFormData((prev) => ({
        ...prev,
        [name]: value,
        [`${name}Display`]: display,
        ...(name === "shippingClientName" && {
          shippingTo: selected?.type === "supplier" ? "supplier" : "client",
        }),
      }));

      saveSingleField({ fieldName: name, nextValue: value, nextDisplay: display, rollback });
      return;
    }

    if (name === "billingAddress" || name === "shippingAddress") {
      if (/undefined|null/i.test(String(value || ""))) {
        toast.error("Unable to save address because the selected address is missing an id.");
        return;
      }

      const opt = allAddresses.find((o) => String(o.id) === String(value));
      const display = opt?.display || opt?.name || "";
      const rollback = {
        [name]: formData[name],
        [`${name}Display`]: formData?.[`${name}Display`] || "",
      };

      setFormData((prev) => ({
        ...prev,
        [name]: value,
        [`${name}Display`]: display,
      }));

      saveSingleField({ fieldName: name, nextValue: value, nextDisplay: display, rollback });
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const applyCreatedCustomerContact = async (createdContact) => {
    const nextOption = buildCustomerContactOption(createdContact);
    if (!nextOption?.id) {
      toast.error("The new contact was created, but its id could not be resolved.");
      return false;
    }

    const nextCustomerContactOptions = mergeOptionsById([
      ...customerContactsOptions,
      nextOption,
    ]);
    const nextContactsOptions = mergeOptionsById([
      ...nextCustomerContactOptions,
      ...(contactsOptions || []).filter((option) => String(option?.source || "") !== "customer"),
    ]);

    setCustomerContactsOptions(nextCustomerContactOptions);
    setContactsOptions(nextContactsOptions);

    const fieldName =
      contactDialog.target === "shipping" ? "shippingClientName" : "billingClientName";
    const displayFieldName =
      fieldName === "shippingClientName"
        ? "shippingClientNameDisplay"
        : "billingClientNameDisplay";
    const previousValue = String(formData[fieldName] ?? "");
    const previousDisplay = String(formData[displayFieldName] ?? "");

    setFormData((prev) => ({
      ...prev,
      [fieldName]: nextOption.id,
      [displayFieldName]: nextOption.name || "",
    }));

    saveSingleField({
      fieldName,
      nextValue: nextOption.id,
      nextDisplay: nextOption.name || "",
      rollback: {
        [fieldName]: previousValue,
        [displayFieldName]: previousDisplay,
      },
    });
    return true;
  };

  const handleCreateCustomerContact = async (payload) => {
    if (!customerId) {
      toast.error("Attach a customer to this order before creating a contact.");
      return;
    }

    try {
      setContactDialog((prev) => ({ ...prev, loading: true }));
      const response = await createCustomerContact(customerId, payload);
      const createdContact = response?.data?.contact;

      if (!createdContact) {
        setContactDialog((prev) => ({ ...prev, loading: false }));
        toast.error("Unable to create the customer contact.");
        return;
      }

      const applied = await applyCreatedCustomerContact(createdContact);
      if (!applied) {
        setContactDialog((prev) => ({ ...prev, loading: false }));
        return;
      }
      toast.success("Customer contact created.");
      setContactDialog({ open: false, target: null, loading: false });
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Unable to create the customer contact."
      );
      setContactDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  const toOptionFromCustomerAddress = (a) => {
    const addressId = getCustomerAddressId(a);
    const optionId = toCustomerAddressOptionId(addressId);

    if (!optionId) {
      console.warn("[sales-order] cannot build customer address option without id", a);
      return null;
    }

    const display = buildDisplay({
      addressName: a?.addressName,
      streetAddress: a?.street || a?.streetAddress,
      suiteApt: a?.suiteApt,
      city: a?.city,
      stateProvince: a?.state || a?.stateProvince,
      postalZipCode: a?.postal || a?.postalZipCode,
      country: a?.country,
    });

    return {
      id: optionId,
      name: a?.addressName || `${a?.street || a?.streetAddress || ""} - ${a?.city || ""}`,
      display,
      source: "customer",
      raw: a,
    };
  };

  const handleCustomerAddressSaved = (saved) => {
    const opt = toOptionFromCustomerAddress(saved);

    if (!opt) {
      toast.error("Address was saved, but no address id was returned.");
      return;
    }

    setAllCustomerAddresses((prev) => {
      const next = [...prev];
      const existsIdx = next.findIndex((x) => String(x.id) === String(opt.id));
      if (existsIdx >= 0) next[existsIdx] = opt;
      else next.push(opt);
      return next;
    });

    if (addrTarget === "billing") {
      setFormData((prev) => ({
        ...prev,
        billingAddress: String(opt.id),
        billingAddressDisplay: opt.display,
      }));
    } else if (addrTarget === "shipping") {
      setFormData((prev) => ({
        ...prev,
        shippingAddress: String(opt.id),
        shippingAddressDisplay: opt.display,
      }));
    }

    setAddAddrOpen(false);
    setAddrTarget(null);
  };

  const getInvoicesByOrderId = async (oid) => {
    const res = await fetchInvoicesListByOrder(oid);
    return Array.isArray(res?.data) ? res.data : [];
  };
  const fetchTermsAndTaxes = async () => {
    try {
      let termsData = null;

      try {
        termsData = await getTerms(instanceUniqueId);
      } catch (error) {
        console.error("Error fetching instance terms:", error);
      }

      if (!termsData && instanceUniqueId) {
        try {
          termsData = await getTerms();
        } catch (error) {
          console.error("Error fetching legacy terms:", error);
        }
      }

      const taxesData = await getTaxes();
      const rawTerms = Array.isArray(termsData?.terms) ? termsData.terms : [];
      const mappedTerms = rawTerms
        .map((term) => ({
          id: String(term?.id ?? term?.TermsId ?? term?.termId ?? ""),
          name:
            term?.name ||
            term?.label ||
            term?.termName ||
            `Term ${term?.id ?? ""}`,
        }))
        .filter((term) => term.id);

      setTermsOptions(mappedTerms);
      setTaxOptions(taxesData?.taxes || []);
    } catch (error) {
      console.error("Error fetching terms or taxes:", error);
    }
  };

  const fetchCustomerDetails = async () => {
    try {
      const currentCustomerId = resolveAttachedCustomerId(order);
      const customerDetails = currentCustomerId
        ? await getCustomerDetails(currentCustomerId)
        : null;

      const customerAddresses = (customerDetails?.customer?.address || [])
        .map((a) => {
          const addressId = getCustomerAddressId(a);
          const optionId = toCustomerAddressOptionId(addressId);

          if (!optionId) {
            console.warn("[sales-order] skipping customer address with missing id", a);
            return null;
          }

          const display =
            buildDisplay(a) ||
            `${a.street || a.streetAddress || ""} ${a.city || ""}`.trim();

          return {
            id: optionId,
            name: a.addressName || `${a.street || a.streetAddress || ""} - ${a.city || ""}`,
            display,
            source: "customer",
            raw: a,
          };
        })
        .filter(Boolean);

      setAllCustomerAddresses(customerAddresses);

      const customerName =
        customerDetails?.customer?.name ||
        customerDetails?.customer?.customerName ||
        customerDetails?.customer?.companyName ||
        customerDetails?.customer?.businessName ||
        resolveAttachedCustomerName(order, "") ||
        "";
      setCustomerDisplayName(customerName);

      const rawCusContacts = customerDetails?.customer?.clientContacts || [];
      const cusContacts = rawCusContacts
        .filter((c) => !c?.mergedIntoContactId)
        .map((c) => {
          const option = buildCustomerContactOption(c);
          if (!option) {
            console.warn("[sales-order] skipping customer contact with missing id", c);
          }
          return option;
        })
        .filter(Boolean);

      setCustomerContactsOptions(cusContacts);

      const addressResponse = await fetchAddressList();
      const globalAddresses = (addressResponse || []).map((a) => {
        const display = buildDisplay(a) || `${a.streetAddress || ""} ${a.city || ""}`;
        return {
          id: String(a.id),
          name: a.addressName || `${a.streetAddress || ""} - ${a.city || ""}`,
          display,
          source: "address",
          raw: a,
        };
      });
      setAllGlobalAddresses(globalAddresses);

      const suppliers = await fetchSuppliersList();

      let thirdPartyAccounts = await getAllClientAccounts({
        instanceUniqueId,
        customerId: currentCustomerId,
      });
      thirdPartyAccounts = Array.isArray(thirdPartyAccounts?.data)
        ? thirdPartyAccounts.data.map((a) => ({ id: a.id, name: a.accountName }))
        : [];

      setContactsOptions(mergeOptionsById([
        ...cusContacts,
        ...suppliers.map((s) => ({ ...s, id: `sup-${s.id}`, type: "supplier" })),
        ...(clientContacts || []).map((c) => ({
          id: `prop-${c.id}`,
          name: c.name || c.email || "Contact",
          email: c.email || "",
          type: "client",
        })),
        ...thirdPartyAccounts.map((t) => ({
          id: `tp-${t.id}`,
          name: t.name || t.accountName,
          type: "third-party",
        })),
      ]));
    } catch (err) {
      console.error("Error fetching customer details:", err);
    }
  };

  const contactOptionsForScope = useMemo(() => {
    if (addressScope !== "customer") return contactsOptions;
    if (customerContactsOptions?.length) return customerContactsOptions;
    return (contactsOptions || []).filter(
      (c) => String(c?.type || "").toLowerCase() === "client"
    );
  }, [addressScope, contactsOptions, customerContactsOptions]);

  const fetchDetails = async () => {
    try {
      let data = await fetchEstimationDetails(tabId);
      data = data?.salesOrder;

      if (!data) return;
      const resolvedStatus = String(
        data?.status ??
          (isOptimisticCreatedDocument ? optimisticCreatedStatus : createModeStatus)
      );

      const findIdByString = (str) => {
        if (!str) return "";
        const m =
          contactsOptions.find((o) => o.name === str) ||
          contactsOptions.find((o) => o.email && o.email === str);
        return m ? String(m.id) : "";
      };

      const contactIds = {
        billing: findIdByString(data?.billingName),
        shipping: findIdByString(data?.shippingName),
      };

      const customerContactPool =
        customerContactsOptions?.length
          ? customerContactsOptions
          : (contactsOptions || []).filter(
              (c) => String(c?.type || "").toLowerCase() === "client"
            );

      const savedBillingContactId = resolveSavedCustomerContactId(
        data?.billingContactId,
        customerContactPool,
        data?.billingName
      );
      const savedShippingContactId = resolveSavedCustomerContactId(
        data?.shippingContactId,
        customerContactPool,
        data?.shippingName
      );

      const findAddrIdByString = (str) => {
        if (!str) return "";
        const m =
          allAddresses.find((o) => o.display === str) ||
          allAddresses.find((o) => o.name === str);
        return m ? String(m.id) : "";
      };

      const billingAddrId = findAddrIdByString(data?.billingAddress);
      const shippingAddrId = findAddrIdByString(data?.shippingAddress);

      const savedBillingAddressId = resolveSavedCustomerAddressId(
        data?.billingAddressId,
        allCustomerAddresses
      );
      const savedShippingAddressId = resolveSavedCustomerAddressId(
        data?.shippingAddressId,
        allCustomerAddresses
      );

      setFormData((prev) => ({
        ...prev,
        status: resolvedStatus,
        commentsEnabled:
          typeof data?.commentsEnabled === "boolean" ? data.commentsEnabled : true,
        orderDate: parseLocalDate(data?.orderDate),
        introduction: data?.introduction || "",
        billingAddress: savedBillingAddressId || billingAddrId,
        billingAddressDisplay: data?.billingAddress || "",
        shippingAddress: savedShippingAddressId || shippingAddrId,
        shippingAddressDisplay: data?.shippingAddress || "",
        billingClientName: savedBillingContactId || contactIds.billing,
        billingClientNameDisplay: data?.billingName || "",
        shippingClientName: savedShippingContactId || contactIds.shipping,
        shippingClientNameDisplay: data?.shippingName || "",
        terms: data?.terms ? parseInt(data.terms) : null,
        currency: data.currency || "USD",
        inHandsDate: parseLocalDate(data?.inhandsDate),
        defaultMargin: data?.defaultMargin || "",
        defaultTax: data?.defaultTax || null,
        customerPO: data?.customerPo || "",
        upfrontMode: data?.upfrontMode ?? null,
        depositType: data?.depositType ?? null,
        depositValue: (data?.depositValue ?? "")?.toString(),
        requireUpfrontPayment: normalizeRequireUpfront(
          data?.paymentStatus === "not_required" ? null : data?.requireUpfrontPayment
        ),
        paymentDueOnSign: normalizeBool(data?.paymentDueOnSign, true),
        depositInvoiceId: data?.depositInvoiceId || null,
        depositAmountSnap: data?.depositAmountSnapshot ?? null,
        depositCurrencySnap: data?.currencySnapshot ?? null,
        paymentStatusSnap: data?.paymentStatus ?? null,
        isSampleOrder: data?.isSampleOrder === true,
      }));

      originalStatusRef.current = resolvedStatus;
      setStatusStageOverride(null);

      const pickArray = (val) => {
        if (Array.isArray(val)) return val;
        if (typeof val === "string") {
          try {
            const parsed = JSON.parse(val);
            return Array.isArray(parsed) ? parsed : [];
          } catch {
            return [];
          }
        }
        return [];
      };

      let history = pickArray(data?.agreementAudit);
      if (!history.length) {
        for (const src of [data?.approvedMeta, data?.audit, data?.audits]) {
          const arr = pickArray(src);
          if (arr.length) {
            history = arr;
            break;
          }
        }
      }

      setAuditLog(history);

      const lastAgree = [...history]
        .reverse()
        .find((e) => String(e?.type).toLowerCase() === "agree");

      const name =
        lastAgree?.agreedBy?.name ||
        lastAgree?.actor?.name ||
        (lastAgree?.agreedBy?.email || lastAgree?.actor?.email || "").split("@")[0] ||
        data?.agreedByName ||
        data?.agreedBy?.name ||
        data?.approvedByName ||
        "";

      const email =
        lastAgree?.agreedBy?.email ||
        lastAgree?.actor?.email ||
        data?.agreedByEmail ||
        data?.agreedBy?.email ||
        data?.approvedByEmail ||
        "";

      const at =
        lastAgree?.at ||
        data?.agreedAt ||
        data?.approvedAt ||
        data?.customerAgreedAt ||
        null;

      setApprovedMeta({ name, email, at });
      setComponentLoading(false);
    } catch (error) {
      console.error("Error fetching estimation details:", error);
    }
  };

  useEffect(() => {
    const nextStatus = isOptimisticCreatedDocument
      ? optimisticCreatedStatus
      : createModeStatus;

    if (!nextStatus) return;
    if (tabId && !isOptimisticCreatedDocument) return;

    setFormData((prev) =>
      String(prev?.status ?? "") === String(nextStatus)
        ? prev
        : {
            ...prev,
            status: nextStatus,
          }
    );
    originalStatusRef.current = String(nextStatus);
    setStatusStageOverride(null);
  }, [
    createModeStatus,
    isOptimisticCreatedDocument,
    optimisticCreatedStatus,
    tabId,
  ]);

  const updateEstimationDetails = async () => {
    try {
      const payload = {
        status: formData.status,
        commentsEnabled: toBool(formData?.commentsEnabled, true),
        orderDate: formData.orderDate.format("YYYY-MM-DD"),
        introduction: formData.introduction,
        billingName: formData.billingClientNameDisplay || "",
        shippingName: formData.shippingClientNameDisplay || "",
        billingAddress: formData.billingAddressDisplay || "",
        shippingAddress: formData.shippingAddressDisplay || "",
        billingContactId: formData.billingClientName || null,
        shippingContactId: formData.shippingClientName || null,
        billingAddressId: formData.billingAddress || null,
        shippingAddressId: formData.shippingAddress || null,
        terms: formData.terms,
        currency: formData.currency,
        inhandsDate: formData.inHandsDate?.isValid?.()
          ? formData.inHandsDate.format("YYYY-MM-DD")
          : null,
        defaultTax: formData.defaultTax,
        customerPo: formData.customerPO,
        orderId,
        shippingTo: formData.shippingTo,
      };

      if (formData.requireUpfrontPayment && !formData.isSampleOrder) {
        Object.assign(payload, {
          requireUpfrontPayment: true,
          upfrontMode: formData.upfrontMode || "deposit",
          depositType: formData.depositType || "percent",
          depositValue: formData.depositValue ? Number(formData.depositValue) : null,
          paymentDueOnSign:
            typeof formData.paymentDueOnSign === "boolean"
              ? formData.paymentDueOnSign
              : true,
        });
      } else {
        Object.assign(payload, {
          requireUpfrontPayment: null,
          upfrontMode: null,
          depositType: null,
          depositValue: null,
          paymentDueOnSign: null,
        });
      }

      if (!validateForm(formData)) {
        return toast.error("Kindly fill the required fields");
      }

      setLoading(true);

      const prevStatus = String(originalStatusRef.current ?? "");
      const nextStatus = String(formData.status ?? "");
      const statusChanged = prevStatus && nextStatus && prevStatus !== nextStatus;

      const res = await updateEstimation(tabId, payload);

      if (statusChanged) {
        originalStatusRef.current = nextStatus;
        setStatusStageOverride(null);
      }

      await fetchDetails();
      if (statusChanged && !isSalesOrderUrl && ["2", "2_sent", "3", "3_sent"].includes(nextStatus)) {
        if (instanceUniqueId) {
          history.replace(`/instance/${instanceUniqueId}/sales/orders/${orderId}/sales-order/${tabId}`);
          window.scrollTo({ top: 0, behavior: "auto" });
        } else {
          console.error("[estimate-promotion] Cannot redirect without instance slug", location.pathname);
        }
      }
      if (typeof fetchOrder === "function") {
        await fetchOrder({ suppressLoader: true });
      }
      toast.success(res?.data?.message || "Sales order changes saved.");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to save sales order changes.");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveUpfrontPayment = async () => {
    if (!isAdmin) {
      toast.error("Contact an administrator to remove upfront payment.");
      return;
    }
    if (!tabId) {
      toast.error("Missing sales order id.");
      return;
    }
    if (!window.confirm("Remove upfront payment and void the current deposit invoice?")) {
      return;
    }

    setRemovingUpfrontPayment(true);
    try {
      await updateEstimation(tabId, {
        orderId,
        status: formData.status,
        requireUpfrontPayment: null,
        upfrontMode: null,
        depositType: null,
        depositValue: null,
        paymentDueOnSign: null,
      });

      setFormData((prev) => ({
        ...prev,
        requireUpfrontPayment: null,
        upfrontMode: null,
        depositType: null,
        depositValue: "",
        paymentDueOnSign: true,
        depositInvoiceId: null,
        depositAmountSnap: null,
        paymentStatusSnap: "not_required",
      }));
      setDepositInfo(null);

      await fetchDetails();
      if (typeof fetchOrder === "function") {
        await fetchOrder({ suppressLoader: true });
      }
      toast.success("Upfront payment removed.");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to remove upfront payment.");
    } finally {
      setRemovingUpfrontPayment(false);
    }
  };

  const submitSampleOrderToggle = async (nextValue) => {
    if (!tabId) {
      toast.error("Missing sales order id.");
      return;
    }

    setSampleOrderSaving(true);
    try {
      const res = await updateEstimation(tabId, {
        orderId,
        status: formData.status,
        isSampleOrder: nextValue,
      });
      const updated = res?.data?.salesOrder || {};

      setFormData((prev) => ({
        ...prev,
        isSampleOrder: updated?.isSampleOrder === true,
        requireUpfrontPayment: updated?.requireUpfrontPayment === true ? true : null,
        upfrontMode: updated?.upfrontMode ?? null,
        depositType: updated?.depositType ?? null,
        depositValue: (updated?.depositValue ?? "")?.toString(),
        paymentDueOnSign: normalizeBool(updated?.paymentDueOnSign, true),
        depositInvoiceId: updated?.depositInvoiceId || null,
        depositAmountSnap: updated?.depositAmountSnapshot ?? null,
        depositCurrencySnap: updated?.currencySnapshot ?? null,
        paymentStatusSnap: updated?.paymentStatus ?? "not_required",
      }));

      if (updated?.depositInvoiceId) {
        await fetchDepositInfo();
      } else {
        setDepositInfo(null);
      }

      await fetchDetails();
      if (typeof fetchOrder === "function") {
        await fetchOrder({ suppressLoader: true });
      }
      toast.success(res?.data?.message || "Sales order updated successfully.");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to update sample order.");
    } finally {
      setSampleOrderSaving(false);
      setSampleOrderDialog({ open: false, nextValue: false });
    }
  };

  const fetchDepositInfo = async () => {
    try {
      if (!orderId) return;
      const invoices = await getInvoicesByOrderId(orderId);

      const parseRel = (r) => {
        if (!r) return {};
        if (typeof r === "string") {
          try {
            return JSON.parse(r);
          } catch {
            return {};
          }
        }
        return r;
      };

      const isDepositInv = (inv) => {
        const rel = parseRel(inv?.invoiceRelationships);
        const role = String(rel.role || rel.type || "").toLowerCase();
        return ["deposit", "deposit-invoice", "deposit_request", "depositrequest"].includes(
          role
        );
      };

      const isLive = (inv) => {
        const active = inv?.isActive;
        const status = String(inv?.invoiceStatus || inv?.paymentStatus || "").toLowerCase();
        return active !== false && !["void", "canceled", "cancelled"].includes(status);
      };

      const pickInvoiceAmount = (inv) => {
        const candidates = [
          inv?.amount,
          inv?.amountDue,
          inv?.total,
          inv?.totalAmount,
          inv?.grandTotal,
        ];
        for (const c of candidates) {
          const n = Number(c);
          if (Number.isFinite(n) && n > 0) return n;
        }
        return null;
      };

      const toInvoiceRank = (inv) => {
        const idNum = Number(inv?.id);
        const ts = new Date(inv?.updatedAt || inv?.createdAt || 0).getTime();
        return {
          idNum: Number.isFinite(idNum) ? idNum : 0,
          ts: Number.isFinite(ts) ? ts : 0,
        };
      };

      const dep =
        [...(invoices || [])]
          .filter((inv) => isDepositInv(inv) && isLive(inv))
          .sort((a, b) => {
            const ra = toInvoiceRank(a);
            const rb = toInvoiceRank(b);
            if (rb.idNum !== ra.idNum) return rb.idNum - ra.idNum;
            return rb.ts - ra.ts;
          })[0] || null;

      if (dep) {
        const resolvedAmount = pickInvoiceAmount(dep);
        setDepositInfo({
          id: dep.id,
          amount: resolvedAmount ?? formData?.depositAmountSnap ?? null,
          currency: String(
            dep.currency || formData?.depositCurrencySnap || formData.currency || "USD"
          ).toUpperCase(),
          status: dep.invoiceStatus || dep.paymentStatus || formData?.paymentStatusSnap || null,
          link: dep.checkoutLink || dep.checkoutUrl || dep.paymentLink || null,
          checkoutSessionId: dep.checkoutSessionId || null,
        });
        setTimeout(() => {
          if (orderId) fetchDepositInfo();
        }, 400);
        return;
      }

      if (formData?.depositInvoiceId || formData?.depositAmountSnap) {
        setDepositInfo({
          id: formData?.depositInvoiceId || null,
          amount: formData?.depositAmountSnap || null,
          currency: String(formData?.depositCurrencySnap || formData.currency || "USD").toUpperCase(),
          status: formData?.paymentStatusSnap || null,
          link: null,
          checkoutSessionId: null,
        });
      } else {
        setDepositInfo(null);
      }
    } catch (e) {
      console.warn("fetchDepositInfo error", e?.message);
      setDepositInfo(null);
    }
  };

  const buildStripeDashboardUrl = (sessionId) => {
    if (!sessionId) return null;
    const isTest = String(sessionId).includes("_test");
    const base = isTest ? "https://dashboard.stripe.com/test" : "https://dashboard.stripe.com";
    return `${base}/checkout/sessions/${sessionId}`;
  };

  const addressOptions = useMemo(() => {
    const pool =
      addressScope === "customer"
        ? allCustomerAddresses
        : addressScope === "global"
        ? allGlobalAddresses
        : allAddresses;

    const q = (addressQuery || "").toLowerCase().trim();
    const filtered = q
      ? pool.filter((o) =>
          `${o.name || ""} ${o.display || ""}`.toLowerCase().includes(q)
        )
      : pool;

    const pinIds = [formData.billingAddress, formData.shippingAddress]
      .map((v) => (v == null ? "" : String(v)))
      .filter(Boolean);

    const pinned = pinIds
      .map((id) => allAddresses.find((o) => String(o.id) === id))
      .filter(Boolean);

    const dedup = new Map();
    [...pinned, ...filtered].forEach((o) => dedup.set(String(o.id), o));
    return Array.from(dedup.values());
  }, [
    addressScope,
    addressQuery,
    allCustomerAddresses,
    allGlobalAddresses,
    allAddresses,
    formData.billingAddress,
    formData.shippingAddress,
  ]);

  useEffect(() => {
    fetchTermsAndTaxes();
  }, []);

  useEffect(() => {
    if (order) {
      fetchCustomerDetails();
    }
  }, [order]);

  useEffect(() => {
    let active = true;

    const loadPendingTermsRequest = async () => {
      if (!shouldShowTermsRequestButton({ isSalesOrderUrl, customerId })) {
        if (active) setPendingTermsRequest(null);
        return;
      }

      try {
        const response = await fetchCustomerTermChangeRequests(customerId);
        if (!active) return;
        setPendingTermsRequest(
          findPendingTermsRequest({
            requests: response?.data?.requests || [],
            orderId,
          })
        );
      } catch (error) {
        if (!active) return;
        console.error("Unable to load customer terms requests.", error);
        setPendingTermsRequest(null);
      }
    };

    loadPendingTermsRequest();

    return () => {
      active = false;
    };
  }, [customerId, isSalesOrderUrl, orderId]);

  useEffect(() => {
    const linkedId = String(order?.clientContactId || "").trim();
    if (!linkedId || !contactsOptions.length) return;

    const linkedContact = contactsOptions.find((opt) => {
      const optId = String(opt?.id || "").trim();
      if (!optId) return false;
      const normalized = optId.split("-").pop();
      return normalized === linkedId && String(opt?.type || "").toLowerCase() === "client";
    });
    if (!linkedContact) return;

    setFormData((prev) => {
      const next = { ...prev };
      let changed = false;

      if (!String(prev.billingClientName || "").trim()) {
        next.billingClientName = linkedContact.id;
        next.billingClientNameDisplay = linkedContact.name || "";
        changed = true;
      }
      if (!String(prev.shippingClientName || "").trim()) {
        next.shippingClientName = linkedContact.id;
        next.shippingClientNameDisplay = linkedContact.name || "";
        changed = true;
      }

      return changed ? next : prev;
    });
  }, [order?.clientContactId, contactsOptions]);

  useEffect(() => {
    if (!hydrated) return;
    if (!tabId) return;
    if (!order?.updatedAt) return;
    fetchDetails();
  }, [order?.updatedAt, hydrated, tabId]);

  useEffect(() => {
    if (!tabId) return;
    if (hydrated) return;
    if (!allAddresses.length || !contactsOptions.length) return;

    let cancelled = false;

    (async () => {
      try {
        await fetchDetails();
        if (!cancelled) setHydrated(true);
      } catch (e) {
        console.error("fetchDetails error", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [tabId, allAddresses.length, contactsOptions.length, hydrated]);

  useEffect(() => {
    if (!hydrated) return;
    if (!orderId) return;
    const status = String(formData.status);
    const shouldFetch =
      (status === "2" && !!formData.requireUpfrontPayment) ||
      status === "3" ||
      !!formData.depositInvoiceId ||
      !!formData.depositAmountSnap;
    if (!shouldFetch) return;
    fetchDepositInfo();
  }, [
    hydrated,
    orderId,
    formData.status,
    formData.requireUpfrontPayment,
    formData.depositInvoiceId,
    formData.depositAmountSnap,
  ]);

  if (loadingComponent) {
    return <LoadingComponent />;
  }

  const isRejected = String(formData.status) === "4";
  const statusValue = String(formData.status || "");

  const getPolicyStatusValue = () => {
    const loadedStatus = String(originalStatusRef.current || "").trim();
    const currentStatus = String(formData.status || "").trim();
    const lockedLoadedStatuses = new Set(["1_sent", "2_sent", "3", "4", "5", "6", "7", "8", "9"]);
    return lockedLoadedStatuses.has(loadedStatus) ? loadedStatus : currentStatus;
  };

  const policyStatusValue = getPolicyStatusValue();
  const isAgreement = policyStatusValue === "2";

  const workflowFromOrder = normalizeSoWorkflowStage(order?.OrderStatus?.SO);
  const workflowFromDocStatus = (() => {
    const status = String(formData.status || "");
    if (status === "1") return "Estimate Draft";
    if (status === "2") return "Agreement Pending";
    if (status === "1_sent") return "Estimate Sent";
    if (status === "2_sent") return "Agreement Sent";
    if (status === "3") return "Client Approved";
    if (status === "4") return "Client Change Requested";
    if (status === "5") return "Closed";
    if (status === "6") return "In Production";
    if (status === "7") return "Shipped";
    if (status === "8") return "Ready to be Invoiced";
    if (status === "9") return "Invoiced";
    return "";
  })();

  const projectSoWorkflowStage = statusStageOverride || workflowFromDocStatus || workflowFromOrder;
  const isEstimateWorkflow = statusValue === "1";
  const isEstimateSentWorkflow = isEstimateWorkflow && projectSoWorkflowStage === "Estimate Sent";
  const isAgreementSentWorkflow =
    statusValue === "2_sent" || projectSoWorkflowStage === "Agreement Sent";

  const policyWorkflowStage =
    policyStatusValue === statusValue
      ? projectSoWorkflowStage
      : normalizeSoWorkflowStageFromStatus(policyStatusValue);

  const statusSelectValue = (() => {
    const baseStatus = String(formData.status || "");
    if (baseStatus === "1" && isEstimateSentWorkflow) return "1_sent";
    if (baseStatus === "2" && isAgreementSentWorkflow) return "2_sent";
    return baseStatus;
  })();

  const statusOptionsForView = (() => {
    const originalStatus = String(originalStatusRef.current ?? "");
    const wasSalesOrderFlow = ["2", "3", "4", "5", "6", "7", "8", "9"].includes(
      originalStatus
    );
    const canReturnToEstimate = originalStatus === "2";
    const currentStatus = String(formData.status || "");
    const hideAgreementStatuses = ["3", "6", "7", "8", "9"].includes(currentStatus);
    const base = !wasSalesOrderFlow
      ? statusOptions
      : statusOptions.filter((opt) => (
          canReturnToEstimate
            ? String(opt.id) !== "1_sent"
            : !["1", "1_sent"].includes(String(opt.id))
        ));
    const withoutAgreementBackflow = hideAgreementStatuses
      ? base.filter((opt) => !["2", "2_sent"].includes(String(opt.id)))
      : base;
    if (!isSalesOrderUrl) return withoutAgreementBackflow;
    return [...withoutAgreementBackflow, ...salesOrderLifecycleOptions].map((option) => (
      ['5', '6', '7', '8', '9'].includes(String(option.id))
        ? { ...option, disabled: !canManageLateSalesOrderStatus }
        : option
    ));
  })();

  const selectedTermsDisplayName = String(
    termsOptions.find((term) => String(term?.id) === String(formData.terms))?.name || ""
  ).trim();
  const selectedTermsName = selectedTermsDisplayName.toLowerCase();
  const isNewCustomerTerm =
    selectedTermsName.includes("new customer") || selectedTermsName.includes("new-customer");
  const shouldShowTermsRequestAction = shouldShowTermsRequestButton({
    isSalesOrderUrl,
    customerId,
  });
  const hasGeneratedDeposit = Boolean(depositInfo?.id || formData?.depositInvoiceId);
  const editStatus = normalizeSalesOrderEditStatus({
    salesOrderStatus: policyStatusValue,
    projectSoStatus: policyWorkflowStage,
  });
  const isAgreementSent = policyStatusValue === "2_sent";
  const canEditPayment = (isAgreement && !isAgreementSent && !isRejected) ||
    (isAdmin && ["1_sent", "2_sent"].includes(policyStatusValue));
  const sampleOrderEditable = canEditSampleOrderStatus(policyStatusValue);
  const paymentConfigEditable =
    canEditPayment && !hasGeneratedDeposit && !formData.isSampleOrder;
  const hasFinalizedInvoice = ["invoiced", "paid", "completed", "succeeded", "success"].includes(
    String(depositInfo?.status || formData?.paymentStatusSnap || "").toLowerCase()
  );
  const sampleOrderDialogCopy = getSampleOrderDialogContent(sampleOrderDialog.nextValue);

  const fieldEditPolicy = (() => {
    const base = {
      comments: false,
      orderDate: false,
      terms: false,
      paymentTermsFields: false,
      currency: false,
      inhandsDate: false,
      defaultTax: false,
      documentTitle: false,
      billingAddressAndContact: false,
      shippingAddressAndContact: false,
    };

    if (editStatus === "invoiced") return base;

    if (editStatus === "estimate-draft") {
      return {
        ...base,
        comments: true,
        orderDate: true,
        terms: true,
        paymentTermsFields: false,
        currency: true,
        inhandsDate: true,
        defaultTax: true,
        documentTitle: true,
        billingAddressAndContact: true,
        shippingAddressAndContact: true,
      };
    }

    if (editStatus === "estimate-sent") {
      return {
        ...base,
        paymentTermsFields: paymentConfigEditable,
        terms: isAdmin,
        defaultTax: isAdmin,
        billingAddressAndContact: true,
        shippingAddressAndContact: true,
      };
    }

    
    if (editStatus === "agreement") {
      return {
        ...base,
        comments: true,
        orderDate: true,
        terms: isAdmin,
        paymentTermsFields: paymentConfigEditable,
        currency: true,
        inhandsDate: true,
        defaultTax: true,
        documentTitle: true,
        billingAddressAndContact: true,
        shippingAddressAndContact: true,
      };
    }

    if (editStatus === "agreement-sent" || policyStatusValue === "2_sent") {
      return {
        ...base,
        // Sent agreements remain locked except for administrator financial corrections.
        paymentTermsFields: paymentConfigEditable,
        terms: isAdmin,
        defaultTax: isAdmin,
        billingAddressAndContact: true,
        shippingAddressAndContact: true,
      };
    }

    if (editStatus === "unknown") return base;

    if (editStatus === "client-approved") {
      return {
        ...base,
        terms: isAdmin,
        currency: true,
        inhandsDate: true,
        defaultTax: true,
        billingAddressAndContact: true,
        shippingAddressAndContact: true,
      };
    }

    if (editStatus === "client-change-requested") {
      return {
        ...base,
        comments: true,
        orderDate: true,
        terms: isAdmin,
        paymentTermsFields: paymentConfigEditable,
        currency: true,
        inhandsDate: true,
        defaultTax: true,
        documentTitle: true,
        billingAddressAndContact: true,
        shippingAddressAndContact: true,
      };
    }

    if (editStatus === "in-production") {
      return {
        ...base,
        terms: isAdmin && !hasFinalizedInvoice,
        currency: !hasFinalizedInvoice,
        inhandsDate: true,
        defaultTax: !hasFinalizedInvoice,
        billingAddressAndContact: true,
        shippingAddressAndContact: true,
      };
    }

    if (editStatus === "shipped") {
      return {
        ...base,
        billingAddressAndContact: true,
        shippingAddressAndContact: true,
      };
    }

    if (editStatus === "ready-to-be-invoiced") {
      return {
        ...base,
        terms: isAdmin && !hasFinalizedInvoice,
        currency: !hasFinalizedInvoice,
        defaultTax: !hasFinalizedInvoice,
        billingAddressAndContact: true,
        shippingAddressAndContact: true,
      };
    }

    return base;
  })();

  const isFieldEditable = (name) => {
    if (name === "status") return true;
    if (name === "commentsEnabled") return !!fieldEditPolicy.comments;
    if (name === "orderDate") return !!fieldEditPolicy.orderDate;
    if (name === "terms") return !!fieldEditPolicy.terms;
    if (name === "currency") return !!fieldEditPolicy.currency;
    if (name === "inHandsDate") return !!fieldEditPolicy.inhandsDate;
    if (name === "defaultTax") return !!fieldEditPolicy.defaultTax;
    if (name === "introduction") return !!fieldEditPolicy.documentTitle;
    if (name === "billingAddress" || name === "billingClientName") {
      return !!fieldEditPolicy.billingAddressAndContact;
    }
    if (name === "shippingAddress" || name === "shippingClientName") {
      return !!fieldEditPolicy.shippingAddressAndContact;
    }
    return true;
  };

  const depositStatusNorm = String(depositInfo?.status || formData?.paymentStatusSnap || "").toLowerCase();
  const isDepositPaid = ["paid", "completed", "succeeded", "success"].includes(depositStatusNorm);
  const showUpfrontDepositNotice =
    isAgreement &&
    formData.requireUpfrontPayment === true &&
    hasGeneratedDeposit &&
    !isDepositPaid;

  const depositDisplayAmount = Number(depositInfo?.amount ?? formData?.depositAmountSnap ?? 0);
  const configuredPercent = Number(formData.depositValue || 0);
  const orderTotalRaw = Number(order?.Total ?? order?.total ?? 0);
  const inferredTotalFromPercent =
    formData.depositType === "percent" &&
    Number.isFinite(configuredPercent) &&
    configuredPercent > 0 &&
    configuredPercent < 100 &&
    Number.isFinite(depositDisplayAmount) &&
    depositDisplayAmount > 0
      ? depositDisplayAmount / (configuredPercent / 100)
      : null;

  const effectiveOrderTotal =
    Number.isFinite(orderTotalRaw) && orderTotalRaw > 0
      ? Math.max(orderTotalRaw, depositDisplayAmount)
      : Number.isFinite(inferredTotalFromPercent)
      ? inferredTotalFromPercent
      : depositDisplayAmount;

  const remainingBalance =
    formData.upfrontMode === "full"
      ? 0
      : Math.max(
          0,
          effectiveOrderTotal -
            (Number.isFinite(depositDisplayAmount) ? depositDisplayAmount : 0)
        );

  const depositPercent = (() => {
    if (
      Number.isFinite(effectiveOrderTotal) &&
      effectiveOrderTotal > 0 &&
      Number.isFinite(depositDisplayAmount) &&
      depositDisplayAmount > 0
    ) {
      return Math.round((depositDisplayAmount / effectiveOrderTotal) * 10000) / 100;
    }
    return Number.isFinite(configuredPercent) && configuredPercent > 0
      ? configuredPercent
      : null;
  })();

  const depositMessageCompanyName =
    order?.instanceName ||
    order?.businessName ||
    order?.CompanyName ||
    "Elite Promotional Marketing";
  const upfrontModeNormalized = String(formData?.upfrontMode || "").trim().toLowerCase();
  const upfrontPaymentMessage =
    upfrontModeNormalized === "full"
      ? "Full payment is required before approval."
      : `${
          depositPercent !== null ? `${depositPercent}% deposit` : "an upfront payment"
        } is required before approval.`;

  const workflowStage = projectSoWorkflowStage ?? "Estimate Draft";
  const orderDate = coerceDayjs(formData?.orderDate);
  const inHandsDate = coerceDayjs(formData?.inHandsDate);
  const hasInHandsDate = !!inHandsDate && inHandsDate.isValid();
  const depositCurrency = String(
    depositInfo?.currency ?? formData?.depositCurrencySnap ?? formData?.currency ?? "CAD"
  ).toUpperCase();

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <WorkflowBanner stage={workflowStage} approvedMeta={latestApprove || approvedMeta} />

        {showUpfrontDepositNotice ? (
          <Alert
            severity="warning"
            icon={<WarningAmberIcon fontSize="small" />}
            sx={{
              borderRadius: "9px",
              fontSize: "0.8125rem",
              "& .MuiAlert-message": { width: "100%" },
            }}
          >
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.25 }}>
              Upfront Payment Required
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
              {upfrontPaymentMessage}
              {depositDisplayAmount > 0
                ? ` Invoice amount: ${depositCurrency} ${Number(depositDisplayAmount).toFixed(2)}.`
                : ""}
              {remainingBalance > 0
                ? ` Remaining balance: ${depositCurrency} ${Number(remainingBalance).toFixed(2)}.`
                : ""}
            </Typography>
          </Alert>
        ) : null}

        <SectionCard
          icon={SettingsOutlinedIcon}
          title="Order Settings"
          subtitle="Status, dates, currency, and document configuration"
          accent
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                lg: "1fr 1fr 1fr 1fr",
              },
              gap: 2,
              mb: 2,
            }}
          >
            <Box>
              <FieldLabel
                required
                hint="Controls which stage this document is in and what actions are available."
                action={paidDepositApprovalAction}
              >
                Status
              </FieldLabel>
              <Select
                fullWidth
                size="small"
                value={statusSelectValue ?? formData.status}
                onChange={(e) => handleSelect(e, "status")}
                IconComponent={KeyboardArrowDownIcon}
                sx={selectSx}
              >
                {statusOptionsForView.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id} disabled={opt.disabled}>
                    <Typography sx={{ fontSize: "0.85rem" }}>{opt.name}</Typography>
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <Box>
              <FieldLabel hint="Controls whether the client can leave comments on the document.">
                Comments Mode
              </FieldLabel>
              <Select
                fullWidth
                size="small"
                value={String(formData?.commentsEnabled ?? true)}
                onChange={(e) => handleSelect(e, "commentsEnabled")}
                disabled={!fieldEditPolicy.comments}
                IconComponent={KeyboardArrowDownIcon}
                sx={selectSx}
              >
                <MenuItem value="true">Comments Enabled</MenuItem>
                <MenuItem value="false">Comments Disabled</MenuItem>
              </Select>
            </Box>

            <Box>
              <FieldLabel>
                <CalendarTodayOutlinedIcon
                  sx={{ fontSize: 11, mr: 0.4, verticalAlign: "middle" }}
                />
                Order Date
              </FieldLabel>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={orderDate}
                  disabled={!fieldEditPolicy.orderDate}
                  onChange={(v) => handleDateChange(coerceDayjs(v), "orderDate")}
                  slots={{ openPickerIcon: CalendarTodayOutlinedIcon }}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      sx: datePickerSx,
                    },
                  }}
                />
              </LocalizationProvider>
            </Box>

            <Box sx={{ position: "relative" }}>
              <FieldSavingSpinner active={fieldSaving.inHandsDate} />
              <FieldLabel required>
                <FlagOutlinedIcon sx={{ fontSize: 11, mr: 0.4, verticalAlign: "middle" }} />
                In Hands Date
              </FieldLabel>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={inHandsDate}
                  disabled={!fieldEditPolicy.inhandsDate || fieldSaving.inHandsDate}
                  minDate={dayjs()}
                  onChange={handleInHandsDateChange}
                  slots={{ openPickerIcon: CalendarTodayOutlinedIcon }}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      error: !hasInHandsDate,
                      sx: datePickerSx,
                    },
                  }}
                />
              </LocalizationProvider>
              {!hasInHandsDate ? (
                <Typography
                  variant="caption"
                  sx={{ color: "error.main", fontSize: "0.68rem", mt: 0.25, display: "block" }}
                >
                  Required for document generation
                </Typography>
              ) : null}
            </Box>
          </Box>

          <Box sx={{ my: 1.5 }}>
            <Chip
              size="small"
              label="Pricing & Terms"
              icon={<AccountBalanceOutlinedIcon sx={{ fontSize: "11px !important" }} />}
              sx={{
                fontSize: "0.68rem",
                fontWeight: 700,
                color: "text.secondary",
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                "& .MuiChip-label": { px: "8px" },
              }}
            />
          </Box>











<Box
  sx={{
    display: "grid",
    gridTemplateColumns: {
      xs: "1fr",
      sm: "1fr 1fr",
      lg: "1fr 1fr 1fr 1fr",
    },
    gap: 2,
  }}
>
  <Box>
    <FieldLabel
      action={
        shouldShowTermsRequestAction ? (
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ ml: "auto" }}>
            {pendingTermsRequest ? (
              <Tooltip
                title={`Terms Request Pending: ${getPendingTermsRequestLabel(pendingTermsRequest)}`}
                arrow
              >
                <Chip
                  size="small"
                  icon={<AccessTimeOutlinedIcon sx={{ fontSize: "12px !important" }} />}
                  label="Terms Request Pending"
                  sx={{
                    height: 20,
                    fontSize: "0.62rem",
                    fontWeight: 700,
                    color: "#92400e",
                    bgcolor: "rgba(245,158,11,0.12)",
                    border: "1px solid rgba(245,158,11,0.28)",
                    "& .MuiChip-label": { px: "6px" },
                    "& .MuiChip-icon": { color: "#d97706" },
                  }}
                />
              </Tooltip>
            ) : null}
            <Tooltip title="Request Terms Change" arrow>
              <IconButton
                size="small"
                aria-label="Request terms change"
                onClick={() => setTermsRequestOpen(true)}
                sx={{
                  width: 28,
                  height: 28,
                  color: "#15803d",
                  bgcolor: "rgba(34,197,94,0.10)",
                  border: "1px solid rgba(34,197,94,0.28)",
                  "&:hover": {
                    bgcolor: "rgba(22,163,74,0.18)",
                    borderColor: "rgba(22,163,74,0.38)",
                  },
                }}
              >
                <RequestQuoteOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        ) : null
      }
    >
      Terms
    </FieldLabel>
    <Select
      fullWidth
      size="small"
      value={formData.terms ?? ""}
      onChange={(e) => handleSelect(e, "terms")}
      disabled={!fieldEditPolicy.terms}
      displayEmpty
      IconComponent={KeyboardArrowDownIcon}
      sx={selectSx}
    >
      <MenuItem value="">
        <Typography sx={{ fontSize: "0.84rem", color: "text.disabled" }}>
          Select terms
        </Typography>
      </MenuItem>
      {termsOptions.map((term) => (
        <MenuItem key={term.id} value={term.id}>
          <Typography sx={{ fontSize: "0.84rem" }}>
            {term.name}
          </Typography>
        </MenuItem>
      ))}
    </Select>
  </Box>

  <Box>
    <FieldLabel>Currency</FieldLabel>
    <Select
      fullWidth
      size="small"
      value={formData.currency ?? "CAD"}
      onChange={(e) => handleSelect(e, "currency")}
      disabled={!fieldEditPolicy.currency}
      IconComponent={KeyboardArrowDownIcon}
      sx={selectSx}
      renderValue={(val) => {
        const opt = currencyOptions.find(
          (o) => String(o.id) === String(val)
        );
        const code = inferFlagCode(opt || { id: val });

        return (
          <Stack direction="row" spacing={0.75} alignItems="center">
            <FlagIcon code={code} size={20} alt={String(val)} />
            <Typography sx={{ fontSize: "0.875rem" }}>
              {opt?.name || val}
            </Typography>
          </Stack>
        );
      }}
    >
      {currencyOptions.map((opt) => {
        const code = inferFlagCode(opt);

        return (
          <MenuItem key={opt.id} value={opt.id}>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <FlagIcon code={code} size={20} alt={opt.name} />
              <Typography sx={{ fontSize: "0.84rem" }}>
                {opt.name}
              </Typography>
            </Stack>
          </MenuItem>
        );
      })}
    </Select>
  </Box>

  <Box>
    <FieldLabel>Default Tax</FieldLabel>
    <Select
      fullWidth
      size="small"
      value={formData.defaultTax ?? ""}
      onChange={(e) => handleSelect(e, "defaultTax")}
      disabled={!fieldEditPolicy.defaultTax}
      displayEmpty
      IconComponent={KeyboardArrowDownIcon}
      sx={selectSx}
    >
      <MenuItem value="">
        <Typography sx={{ fontSize: "0.84rem", color: "text.disabled" }}>
          Select tax
        </Typography>
      </MenuItem>
      {taxOptions.map((tax) => (
        <MenuItem key={tax.id} value={tax.id}>
          <Typography sx={{ fontSize: "0.84rem" }}>
            {tax.name || (tax.rate != null ? `Tax (${Number(tax.rate) * 100}%)` : "Tax")}
          </Typography>
        </MenuItem>
      ))}
    </Select>
  </Box>

  <Box sx={{display: "none"}}>
    <FieldLabel>Customer PO</FieldLabel>
    <TextField
      fullWidth
      size="small"
      value={formData.customerPO ?? ""}
      onChange={(e) => handleInputChange(e, "customerPO")}
      placeholder="Enter PO number"
      sx={inputSx}
    />
  </Box>
</Box>
</SectionCard>











<SectionCard
  icon={ContactMailOutlinedIcon}
  title="Billing & Shipping"
  subtitle="Client contacts, billing address, and shipping destination"
>
  <Box sx={{ mb: 2 }}>
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      justifyContent="space-between"
      flexWrap="wrap"
      sx={{ mb: 1.25, gap: 1 }}
    >
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 0.35,
          }}
        >
          Address Source
        </Typography>
        <Tooltip title="Customer addresses are linked to this customer. Global addresses are shared saved addresses." arrow>
          <InfoOutlinedIcon sx={{ fontSize: 13, color: "text.disabled" }} />
        </Tooltip>
      </Stack>

      <Stack direction="row" spacing={0.75} alignItems="center">
        {["customer", "global", "all"].map((scope) => (
          <Chip
            key={scope}
            label={addressScopeLabels[scope]}
            size="small"
            onClick={() => setAddressScope(scope)}
            icon={
              scope === "customer" ? (
                <PeopleAltOutlinedIcon sx={{ fontSize: "12px !important" }} />
              ) : scope === "global" ? (
                <PublicOutlinedIcon sx={{ fontSize: "12px !important" }} />
              ) : (
                <AllInboxOutlinedIcon sx={{ fontSize: "12px !important" }} />
              )
            }
            sx={{
              height: 26,
              fontSize: "0.72rem",
              fontWeight: 700,
              cursor: "pointer",
              bgcolor:
                addressScope === scope
                  ? "rgba(199,9,9,0.08)"
                  : "background.paper",
              color: addressScope === scope ? "primary.main" : "text.secondary",
              border: "1px solid",
              borderColor:
                addressScope === scope ? "rgba(199,9,9,0.25)" : "divider",
              "& .MuiChip-icon": {
                ml: "6px",
                color: addressScope === scope ? "primary.main" : "text.disabled",
              },
            }}
          />
        ))}
      </Stack>
    </Stack>

    <TextField
      fullWidth
      size="small"
      value={addressQuery}
      onChange={(e) => setAddressQuery(e.target.value)}
      placeholder="Search addresses by name, city, street..."
      sx={inputSx}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon sx={{ fontSize: 17, color: "text.disabled" }} />
          </InputAdornment>
        ),
        endAdornment: addressQuery ? (
          <InputAdornment position="end">
            <IconButton size="small" onClick={() => setAddressQuery("")}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
    />
  </Box>

  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
      gap: 2.25,
    }}
  >
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "9px",
        bgcolor: "rgba(13,94,166,0.015)",
      }}
    >
      <Typography
        variant="body2"
        fontWeight={700}
        sx={{ mb: 1.5, color: "text.primary", fontSize: "0.85rem" }}
      >
        Billing
      </Typography>

      <Stack spacing={1.75}>
        <Box sx={{ position: "relative" }}>
          <FieldLabel
            action={
              <>
                <FieldSavingSpinner active={fieldSaving.billingClientName} inline />
                <Tooltip title="Add contact" arrow>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => openAddContactDialog("billing")}
                    disabled={
                      !customerId ||
                      !fieldEditPolicy.billingAddressAndContact ||
                      fieldSaving.billingClientName
                    }
                    sx={{
                      p: 0.35,
                      color: "text.disabled",
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: "7px",
                      "&:hover": {
                        color: "primary.main",
                        borderColor: "primary.light",
                        bgcolor: "action.hover",
                      },
                    }}
                  >
                    <AddCircleOutlineIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </span>
              </Tooltip>
              </>
            }
          >
            Billing Contact
          </FieldLabel>
          <Select
            fullWidth
            size="small"
            value={formData.billingClientName ?? ""}
            onChange={(e) => handleSelect(e, "billingClientName")}
            displayEmpty
            disabled={
              !fieldEditPolicy.billingAddressAndContact ||
              fieldSaving.billingClientName
            }
            IconComponent={KeyboardArrowDownIcon}
            renderValue={(val) => {
              const found =
                contactOptionsForScope.find((o) => String(o.id) === String(val)) ||
                contactsOptions.find((o) => String(o.id) === String(val));
              return found ? (
                <Typography sx={{ fontSize: "0.875rem" }}>
                  {found.name}
                </Typography>
              ) : (
                <Typography sx={{ fontSize: "0.875rem", color: "text.disabled" }}>
                  Select billing contact
                </Typography>
              );
            }}
            sx={selectSx}
          >
            <MenuItem value="">
              <Typography sx={{ fontSize: "0.84rem", color: "text.disabled" }}>
                Select billing contact
              </Typography>
            </MenuItem>
            {contactOptionsForScope.map((c) => (
              <MenuItem key={c.id} value={String(c.id)}>
                <Box>
                  <Typography sx={{ fontSize: "0.84rem", fontWeight: 600 }}>
                    {c.name}
                  </Typography>
                  {c.email ? (
                    <Typography sx={{ fontSize: "0.72rem", color: "text.secondary" }}>
                      {c.email}
                    </Typography>
                  ) : null}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </Box>

        <AddressField
          label="Billing Address"
          value={formData.billingAddress}
          options={addressOptions}
          onChange={(value) => handleSelect({ target: { value } }, "billingAddress")}
          onAddNew={() => openAddAddress("billing")}
          disabled={!fieldEditPolicy.billingAddressAndContact}
          saving={fieldSaving.billingAddress}
          emptyText="Select billing address"
          helperText={formData.billingAddressDisplay}
        />
      </Stack>
    </Paper>

    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "9px",
        bgcolor: "rgba(34,197,94,0.015)",
      }}
    >
      <Typography
        variant="body2"
        fontWeight={700}
        sx={{ mb: 1.5, color: "text.primary", fontSize: "0.85rem" }}
      >
        Shipping
      </Typography>

      <Stack spacing={1.75}>
        <Box sx={{ position: "relative" }}>
          <FieldLabel
            action={
              <>
                <FieldSavingSpinner active={fieldSaving.shippingClientName} inline />
                <Tooltip title="Add contact" arrow>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => openAddContactDialog("shipping")}
                    disabled={
                      !customerId ||
                      !fieldEditPolicy.shippingAddressAndContact ||
                      fieldSaving.shippingClientName
                    }
                    sx={{
                      p: 0.35,
                      color: "text.disabled",
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: "7px",
                      "&:hover": {
                        color: "primary.main",
                        borderColor: "primary.light",
                        bgcolor: "action.hover",
                      },
                    }}
                  >
                    <AddCircleOutlineIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </span>
              </Tooltip>
              </>
            }
          >
            Main Shipping Contact
          </FieldLabel>
          <Select
            fullWidth
            size="small"
            value={formData.shippingClientName ?? ""}
            onChange={(e) => handleSelect(e, "shippingClientName")}
            displayEmpty
            disabled={
              !fieldEditPolicy.shippingAddressAndContact ||
              fieldSaving.shippingClientName
            }
            IconComponent={KeyboardArrowDownIcon}
            renderValue={(val) => {
              const found = contactOptionsForScope.find(
                (o) => String(o.id) === String(val)
              );
              return found ? (
                <Typography sx={{ fontSize: "0.875rem" }}>
                  {found.name}
                </Typography>
              ) : (
                <Typography sx={{ fontSize: "0.875rem", color: "text.disabled" }}>
                  Select shipping contact
                </Typography>
              );
            }}
            sx={selectSx}
          >
            <MenuItem value="">
              <Typography sx={{ fontSize: "0.84rem", color: "text.disabled" }}>
                Select shipping contact
              </Typography>
            </MenuItem>
            {contactOptionsForScope.map((c) => (
              <MenuItem key={c.id} value={String(c.id)}>
                <Box>
                  <Typography sx={{ fontSize: "0.84rem", fontWeight: 600 }}>
                    {c.name}
                  </Typography>
                  {c.email ? (
                    <Typography sx={{ fontSize: "0.72rem", color: "text.secondary" }}>
                      {c.email}
                    </Typography>
                  ) : null}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </Box>

        <AddressField
          label="Main Shipping Address"
          value={formData.shippingAddress}
          options={addressOptions}
          onChange={(value) => handleSelect({ target: { value } }, "shippingAddress")}
          onAddNew={() => openAddAddress("shipping")}
          disabled={!fieldEditPolicy.shippingAddressAndContact}
          saving={fieldSaving.shippingAddress}
          emptyText="Select shipping address"
          helperText={formData.shippingAddressDisplay}
        />
      </Stack>
    </Paper>
  </Box>

  <Box sx={{ mt: 2 }}>
    <Button
      variant="outlined"
      size="small"
      onClick={openThirdPartyModal}
      startIcon={<AddCircleOutlineIcon sx={{ fontSize: 15 }} />}
      sx={{
        textTransform: "none",
        borderRadius: "8px",
        fontWeight: 700,
        fontSize: "0.8rem",
      }}
    >
      Manage Third-Party Shipping Accounts
    </Button>
  </Box>
</SectionCard>

<SectionCard
  icon={CreditCardOutlinedIcon}
  title="Upfront Payment"
  subtitle="Configure deposit or full payment requirements before client approval"
  accent={formData.requireUpfrontPayment === true}
  action={
    formData.requireUpfrontPayment === true ? (
      <DepositBadge status={depositInfo?.status || formData?.paymentStatusSnap} />
    ) : null
  }
>
  <Stack spacing={1.75}>
    <Paper
      elevation={0}
      onClick={() => {
        if (!paymentConfigEditable || !fieldEditPolicy.paymentTermsFields || formData.isSampleOrder) return;
        setFormData((prev) => ({
          ...prev,
          requireUpfrontPayment: prev.requireUpfrontPayment ? null : true,
          upfrontMode: prev.requireUpfrontPayment ? null : prev.upfrontMode || "deposit",
          depositType: prev.requireUpfrontPayment ? null : prev.depositType || "percent",
          depositValue: prev.requireUpfrontPayment ? "" : prev.depositValue || "50",
          paymentDueOnSign: prev.requireUpfrontPayment ? true : prev.paymentDueOnSign ?? true,
        }));
      }}
      sx={{
        p: 1.5,
        border: "1px solid",
        borderColor:
          formData.requireUpfrontPayment === true
            ? "rgba(199,9,9,0.25)"
            : "divider",
        borderRadius: "9px",
        bgcolor:
          formData.requireUpfrontPayment === true
            ? "rgba(199,9,9,0.035)"
            : "background.paper",
        cursor:
          paymentConfigEditable && fieldEditPolicy.paymentTermsFields && !formData.isSampleOrder
            ? "pointer"
            : "not-allowed",
        opacity:
          paymentConfigEditable && fieldEditPolicy.paymentTermsFields && !formData.isSampleOrder
            ? 1
            : 0.65,
        transition: "border-color 0.15s, background-color 0.15s",
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="body2" fontWeight={700} sx={{ fontSize: "0.86rem" }}>
            Require Upfront Payment
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.73rem" }}>
            Request a full payment or deposit before the sales order can be approved.
          </Typography>
        </Box>

        <Switch
          checked={formData.requireUpfrontPayment === true}
          disabled={!paymentConfigEditable || !fieldEditPolicy.paymentTermsFields || formData.isSampleOrder}
          onChange={(e) => {
            e.stopPropagation();
            setFormData((prev) => ({
              ...prev,
              requireUpfrontPayment: e.target.checked ? true : null,
              upfrontMode: e.target.checked ? prev.upfrontMode || "deposit" : null,
              depositType: e.target.checked ? prev.depositType || "percent" : null,
              depositValue: e.target.checked ? prev.depositValue || "50" : "",
              paymentDueOnSign: e.target.checked ? prev.paymentDueOnSign ?? true : true,
            }));
          }}
        />
      </Stack>
    </Paper>

    {formData.isSampleOrder ? (
      <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.73rem" }}>
        {SAMPLE_ORDER_UPFRONT_DISABLED_HELPER}
      </Typography>
    ) : null}

    <Collapse in={formData.requireUpfrontPayment === true} timeout="auto" unmountOnExit>
      <Stack spacing={1.75}>
        {hasGeneratedDeposit ? (
          <Alert
            severity={isDepositPaid ? "success" : "info"}
            sx={{ borderRadius: "9px", fontSize: "0.8rem" }}
            action={
              <Stack direction="row" spacing={0.75}>
                {depositInfo?.checkoutSessionId ? (
                  <Button
                    size="small"
                    endIcon={<OpenInNewIcon sx={{ fontSize: 13 }} />}
                    onClick={() => {
                      const url = buildStripeDashboardUrl(depositInfo.checkoutSessionId);
                      if (url) window.open(url, "_blank", "noopener,noreferrer");
                    }}
                    sx={{ textTransform: "none", fontWeight: 700 }}
                  >
                    Stripe
                  </Button>
                ) : null}

                {!isDepositPaid ? (
                  <Tooltip
                    title={isAdmin ? "" : "Contact an administrator to remove upfront payment."}
                    arrow
                  >
                    <span>
                      <Button
                        size="small"
                        color="error"
                        startIcon={
                          removingUpfrontPayment ? null : (
                            <RemoveCircleOutlineIcon sx={{ fontSize: 13 }} />
                          )
                        }
                        disabled={!isAdmin || removingUpfrontPayment}
                        onClick={handleRemoveUpfrontPayment}
                        sx={{ textTransform: "none", fontWeight: 700 }}
                      >
                        {removingUpfrontPayment ? "Removing..." : isAdmin ? "Remove" : "Disabled"}
                      </Button>
                    </span>
                  </Tooltip>
                ) : null}
              </Stack>
            }
          >
            <Typography variant="body2" fontWeight={700}>
              {isDepositPaid ? "Payment completed" : "Deposit invoice generated"}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {depositInfo?.amount || formData.depositAmountSnap
                ? `${depositCurrency} ${Number(
                    depositInfo?.amount || formData.depositAmountSnap
                  ).toFixed(2)}`
                : "Amount pending"}
              {depositInfo?.status || formData.paymentStatusSnap
                ? ` · ${normalizePaymentStatusLabel(
                    depositInfo?.status || formData.paymentStatusSnap
                  )}`
                : ""}
            </Typography>
          </Alert>
        ) : null}

        <Box
          sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
          gap: 1.75,
        }}
      >
        <Box>
          <FieldLabel>Payment Mode</FieldLabel>
          <Select
            fullWidth
            size="small"
            value={formData.upfrontMode ?? "deposit"}
            onChange={(e) => handleSelect(e, "upfrontMode")}
            disabled={!paymentConfigEditable || !fieldEditPolicy.paymentTermsFields}
            IconComponent={KeyboardArrowDownIcon}
            sx={selectSx}
          >
            <MenuItem value="full">Full amount</MenuItem>
            <MenuItem value="deposit">Deposit only</MenuItem>
          </Select>
        </Box>

        <Box>
          <FieldLabel>Deposit Type</FieldLabel>
          <Select
            fullWidth
            size="small"
            value={formData.depositType ?? "percent"}
            onChange={(e) => handleSelect(e, "depositType")}
            disabled={
              !paymentConfigEditable ||
              !fieldEditPolicy.paymentTermsFields ||
              formData.upfrontMode === "full"
            }
            IconComponent={KeyboardArrowDownIcon}
            sx={selectSx}
          >
            <MenuItem value="percent">% of total</MenuItem>
            <MenuItem value="fixed">Fixed amount</MenuItem>
          </Select>
        </Box>

        <Box>
          <FieldLabel>
            {formData.depositType === "fixed" ? "Deposit Amount" : "Deposit %"}
          </FieldLabel>
          <TextField
            fullWidth
            size="small"
            type="number"
            value={formData.depositValue ?? ""}
            onChange={(e) => handleInputChange(e, "depositValue")}
            disabled={
              !paymentConfigEditable ||
              !fieldEditPolicy.paymentTermsFields ||
              formData.upfrontMode === "full"
            }
            placeholder={formData.depositType === "fixed" ? "Amount" : "Percent"}
            sx={inputSx}
            InputProps={{
              startAdornment:
                formData.depositType === "fixed" ? (
                  <InputAdornment position="start">
                    <AttachMoneyIcon sx={{ fontSize: 15, color: "text.disabled" }} />
                  </InputAdornment>
                ) : null,
              endAdornment:
                formData.depositType !== "fixed" ? (
                  <InputAdornment position="end">
                    <PercentIcon sx={{ fontSize: 15, color: "text.disabled" }} />
                  </InputAdornment>
                ) : null,
            }}
          />
        </Box>
      </Box>

      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={!!formData.paymentDueOnSign}
            disabled={!paymentConfigEditable || !fieldEditPolicy.paymentTermsFields}
            onChange={handleToggle("paymentDueOnSign")}
          />
        }
        label={
          <Typography sx={{ fontSize: "0.82rem", color: "text.secondary" }}>
            Payment due when client signs agreement
          </Typography>
        }
      />
    </Stack>
  </Collapse>
</Stack>
</SectionCard>

<SectionCard
  icon={SettingsOutlinedIcon}
  title="Sample Order"
  subtitle="Disable deposit and upfront payment requirements for sample orders"
  accent={formData.isSampleOrder === true}
>
  <Stack spacing={1.75}>
    <Paper
      elevation={0}
      onClick={() => {
        if (!sampleOrderEditable || sampleOrderSaving) return;
        setSampleOrderDialog({
          open: true,
          nextValue: !formData.isSampleOrder,
        });
      }}
      sx={{
        p: 1.5,
        border: "1px solid",
        borderColor:
          formData.isSampleOrder === true
            ? "rgba(199,9,9,0.25)"
            : "divider",
        borderRadius: "9px",
        bgcolor:
          formData.isSampleOrder === true
            ? "rgba(199,9,9,0.035)"
            : "background.paper",
        cursor: sampleOrderEditable && !sampleOrderSaving ? "pointer" : "not-allowed",
        opacity: sampleOrderEditable && !sampleOrderSaving ? 1 : 0.65,
        transition: "border-color 0.15s, background-color 0.15s",
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="body2" fontWeight={700} sx={{ fontSize: "0.86rem" }}>
            Sample Order
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.73rem" }}>
            {getSampleOrderStageHelperText(policyStatusValue)}
          </Typography>
        </Box>

        <Switch
          checked={formData.isSampleOrder === true}
          disabled={!sampleOrderEditable || sampleOrderSaving}
          onChange={(e) => {
            e.stopPropagation();
            if (!sampleOrderEditable || sampleOrderSaving) return;
            setSampleOrderDialog({
              open: true,
              nextValue: e.target.checked,
            });
          }}
        />
      </Stack>
    </Paper>
  </Stack>
</SectionCard>

<SectionCard
  icon={AssignmentOutlinedIcon}
  title="Document Title / Introduction"
  subtitle="Optional client-facing introduction shown on generated documents"
>
  <TextField
    fullWidth
    multiline
    minRows={4}
    value={formData.introduction ?? ""}
    onChange={(e) => handleInputChange(e, "introduction")}
    disabled={!fieldEditPolicy.documentTitle}
    placeholder="Add a short introduction or note for the client..."
    sx={{
      "& .MuiOutlinedInput-root": {
        borderRadius: "9px",
        fontSize: "0.875rem",
        alignItems: "flex-start",
        backgroundColor: "background.paper",
        "& fieldset": { borderColor: "divider" },
        "&:hover fieldset": { borderColor: "text.disabled" },
        "&.Mui-focused fieldset": {
          borderColor: "primary.main",
          borderWidth: "1.5px",
        },
      },
    }}
  />
</SectionCard>

<Paper
  elevation={0}
  sx={{
    position: "sticky",
    bottom: 0,
    zIndex: 5,
    border: "1px solid",
    borderColor: "divider",
    borderRadius: "10px 10px 0 0",
    p: 1.25,
    bgcolor: "background.paper",
    boxShadow: "0 -4px 18px rgba(15,23,42,0.08)",
  }}
>
  <Stack
    direction={{ xs: "column", sm: "row" }}
    alignItems={{ xs: "stretch", sm: "center" }}
    justifyContent="space-between"
    spacing={1}
  >
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
      <LockOutlinedIcon sx={{ fontSize: 15, color: "text.disabled" }} />
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
          fontSize: "0.72rem",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {getSalesOrderEditStatusMessage(editStatus)}
      </Typography>
    </Stack>

    <Button
      variant="contained"
      onClick={updateEstimationDetails}
      disabled={loading}
      startIcon={loading ? null : <SaveOutlinedIcon sx={{ fontSize: 15 }} />}
      sx={{
        textTransform: "none",
        fontWeight: 800,
        borderRadius: "8px",
        height: 38,
        px: 2.5,
        boxShadow: "none",
        minWidth: 150,
      }}
    >
      {loading ? <ButtonLoader /> : "Save Changes"}
    </Button>
  </Stack>
</Paper>
</Box>

<AddCustomerAddressModal
  open={addAddrOpen}
  onClose={() => {
    setAddAddrOpen(false);
    setAddrTarget(null);
  }}
  customerId={customerId}
  onSaved={handleCustomerAddressSaved}
/>

<CustomerContactDialog
  open={contactDialog.open}
  loading={contactDialog.loading}
  onClose={closeAddContactDialog}
  onSubmit={handleCreateCustomerContact}
  targetLabel={contactDialogTargetLabel}
/>

<ThirdPartyShippingAccount
  open={clientModalOpen}
  onClose={closeThirdPartyModal}
  customerId={customerId}
  instanceUniqueId={instanceUniqueId}
/>
<TermsChangeRequestDialog
  open={termsRequestOpen}
  onClose={() => setTermsRequestOpen(false)}
  customerId={customerId}
  customerName={customerDisplayName || resolveAttachedCustomerName(order, "")}
  orderId={orderId}
  projectName={order?.ProjectName || order?.projectName || ""}
  currentTermId={formData.terms}
  currentTermName={selectedTermsDisplayName}
  termsOptions={termsOptions}
  onSubmitted={(request) => {
    setPendingTermsRequest(request || null);
    setTermsRequestOpen(false);
  }}
/>
<Dialog
  open={sampleOrderDialog.open}
  onClose={() => {
    if (sampleOrderSaving) return;
    setSampleOrderDialog({ open: false, nextValue: false });
  }}
  maxWidth="sm"
  fullWidth
>
  <DialogTitle>{sampleOrderDialogCopy.title}</DialogTitle>
  <DialogContent dividers>
    <Typography sx={{ fontSize: "0.92rem", color: "text.secondary" }}>
      {sampleOrderDialogCopy.message}
    </Typography>
  </DialogContent>
  <DialogActions>
    <Button
      onClick={() => setSampleOrderDialog({ open: false, nextValue: false })}
      disabled={sampleOrderSaving}
      sx={{ textTransform: "none" }}
    >
      Cancel
    </Button>
    <Button
      variant="contained"
      color={sampleOrderDialog.nextValue ? "warning" : "primary"}
      onClick={() => submitSampleOrderToggle(sampleOrderDialog.nextValue)}
      disabled={sampleOrderSaving}
      sx={{ textTransform: "none", fontWeight: 700 }}
    >
      {sampleOrderSaving ? <ButtonLoader /> : sampleOrderDialogCopy.confirmLabel}
    </Button>
  </DialogActions>
</Dialog>
</>
);
};

export default Estimation;
